import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, TOKEN_KEY, useAuth } from '../contexts/AuthContext';
import InfoFluxoAtendimento from '../components/InfoFluxoAtendimento';
import { playNotificationSound, unlockNotificationSound } from '../helpers/notificationSound';
import {
  segundosEmJanela,
  dentroDaJanela,
  formatarCronometro,
} from '../helpers/janelaAtendimento';

const STATUS_LABEL = {
  aberta: 'Aberta',
  em_atendimento: 'Em atendimento',
  encerrada: 'Encerrada',
};

const STATUS_COLOR = {
  aberta: '#fe8d25',
  em_atendimento: '#2A8A9C',
  encerrada: '#5E7A82',
};

function formatHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const hoje = new Date();
  const mesmoDia = d.toDateString() === hoje.toDateString();
  if (mesmoDia) {
    return hora;
  }
  const data = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${data} ${hora}`;
}

function formatHoraCompleta(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function formatNumero(numero) {
  if (!numero) return '';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  if (limpo.length === 12) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 8)}-${limpo.slice(8)}`;
  }
  return numero;
}

function iniciaisDe(nome, numero) {
  const base = (nome || '').trim();
  if (base) {
    const partes = base.split(/\s+/).slice(0, 2);
    return partes.map((p) => p[0]?.toUpperCase()).join('');
  }
  return numero ? String(numero).slice(-2) : '?';
}

// Persiste a velocidade escolhida por-usuario (localStorage). Nas 3
// opcoes o valor cabe num scroll do polegar; passar disso vira gimmick.
const VELOCIDADES_AUDIO = [1, 1.5, 2];
function AudioBubble({ url, out }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [tempoAtual, setTempoAtual] = useState(0);
  const [velocidade, setVelocidade] = useState(() => {
    const salvo = Number(localStorage.getItem('ti_atend_audio_speed'));
    return VELOCIDADES_AUDIO.includes(salvo) ? salvo : 1;
  });

  const BARRAS = 28;
  const alturas = useMemo(() => {
    const seed = (url || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    let s = seed || 42;
    return Array.from({ length: BARRAS }, () => {
      s = (s * 1664525 + 1013904223) & 0x7fffffff;
      return 4 + (s % 13);
    });
  }, [url]);

  function fmtTempo(s) {
    if (!s || isNaN(s) || !isFinite(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  }

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    playing ? el.pause() : el.play();
  }

  function seekByClick(e) {
    const el = audioRef.current;
    if (!el || !duracao) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duracao;
  }

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setProgress(0); setTempoAtual(0); };
    const onMeta = () => setDuracao(el.duration || 0);
    const onTime = () => {
      setTempoAtual(el.currentTime);
      if (el.duration) setProgress((el.currentTime / el.duration) * 100);
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('timeupdate', onTime);
    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('timeupdate', onTime);
    };
  }, []);

  useEffect(() => {
    // Aplica no elemento e persiste. Um usuario que ativa 2x um audio quer
    // 2x nos proximos tambem — vale pra transcricao rapida.
    if (audioRef.current) audioRef.current.playbackRate = velocidade;
    localStorage.setItem('ti_atend_audio_speed', String(velocidade));
  }, [velocidade]);

  function ciclarVelocidade() {
    const idx = VELOCIDADES_AUDIO.indexOf(velocidade);
    setVelocidade(VELOCIDADES_AUDIO[(idx + 1) % VELOCIDADES_AUDIO.length]);
  }

  const corBotao = out ? '#a8e6cf' : '#6bcfb5';
  const progressoIdx = Math.floor((progress / 100) * BARRAS);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: 240, userSelect: 'none' }}>
      <audio ref={audioRef} src={url} preload="metadata" style={{ display: 'none' }} />
      <button
        type="button"
        onClick={togglePlay}
        style={{
          width: 38, height: 38, borderRadius: '50%',
          background: corBotao, border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, color: '#1a4a3a', fontSize: '1.1rem',
        }}
      >
        <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`}
           style={{ marginLeft: playing ? 0 : 2 }} />
      </button>
      <div style={{ flex: 1 }}>
        <div
          role="slider"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 20, cursor: 'pointer' }}
          onClick={seekByClick}
          onKeyDown={(e) => {
            const el = audioRef.current;
            if (!el) return;
            if (e.key === 'ArrowRight') el.currentTime = Math.min(el.currentTime + 5, el.duration);
            if (e.key === 'ArrowLeft') el.currentTime = Math.max(el.currentTime - 5, 0);
          }}
        >
          {alturas.map((h, i) => (
            <div key={i} style={{
              flex: 1, height: h, borderRadius: 2,
              background: i < progressoIdx ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)',
              transition: 'background 0.08s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          <i className="bi bi-mic-fill" style={{ fontSize: '0.65rem', opacity: 0.55 }} />
          <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
            {playing || progress > 0 ? fmtTempo(tempoAtual) : fmtTempo(duracao)}
          </span>
          <button
            type="button"
            onClick={ciclarVelocidade}
            title="Velocidade de reproducao"
            style={{
              marginLeft: 'auto',
              padding: '1px 6px',
              fontSize: '0.65rem',
              fontWeight: 600,
              lineHeight: 1.2,
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 10,
              background: velocidade === 1 ? 'transparent' : 'rgba(255,255,255,0.18)',
              color: '#fff',
              cursor: 'pointer',
              opacity: velocidade === 1 ? 0.6 : 0.95,
            }}
          >
            {velocidade === 1 ? '1x' : `${velocidade}x`}
          </button>
        </div>
      </div>
    </div>
  );
}

function MidiaRenderer({ mensagem, onAbrirImagem, out }) {
  // URLs do WhatsApp (mmg.whatsapp.net etc.) servem o blob criptografado
  // direto, sem mediaKey, e baixar resulta em arquivo .enc inutil.
  // So aceitamos signed URL do nosso storage ou URL publica de outra origem.
  const ehUrlWhatsApp = (u) => /(?:whatsapp\.net|wa\.me)/i.test(u || '');
  const url = mensagem.midia_signed_url
    || (mensagem.midia_url && /^https?:\/\//i.test(mensagem.midia_url) && !ehUrlWhatsApp(mensagem.midia_url)
      ? mensagem.midia_url
      : null);
  const tipo = mensagem.tipo;

  // Sem URL utilizavel - mostra placeholder explicativo
  if (!url) {
    const labelMap = {
      imagem: 'imagem', audio: 'audio', video: 'video',
      documento: 'documento', figurinha: 'figurinha',
    };
    const motivoEnc = ehUrlWhatsApp(mensagem.midia_url || '');
    return (
      <div style={{ fontStyle: 'italic', opacity: 0.85, fontSize: '0.85rem' }}>
        <i className="bi bi-exclamation-circle me-1" />
        {labelMap[tipo] || 'midia'} indisponivel
        {motivoEnc && (
          <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: 2 }}>
            (download nao chegou ao storage)
          </div>
        )}
      </div>
    );
  }

  if (tipo === 'imagem') {
    return (
      <button
        type="button"
        onClick={() => onAbrirImagem?.(url)}
        title="Clique para ampliar"
        style={{
          padding: 0, border: 'none', background: 'transparent',
          display: 'block', cursor: 'zoom-in',
        }}
      >
        <img
          src={url}
          alt="imagem"
          style={{
            maxWidth: 280, maxHeight: 280, borderRadius: 8,
            display: 'block',
          }}
        />
      </button>
    );
  }
  if (tipo === 'figurinha') {
    // Figurinhas do WhatsApp sao image/webp (estaticas ou animadas).
    // Renderiza menor que uma imagem comum, sem fundo de balao.
    return (
      <button
        type="button"
        onClick={() => onAbrirImagem?.(url)}
        title="Figurinha (clique para ampliar)"
        style={{
          padding: 0, border: 'none', background: 'transparent',
          display: 'block', cursor: 'zoom-in',
        }}
      >
        <img
          src={url}
          alt="figurinha"
          style={{
            maxWidth: 140, maxHeight: 140, display: 'block',
          }}
        />
      </button>
    );
  }
  if (tipo === 'audio') {
    return <AudioBubble url={url} out={out} />;
  }
  if (tipo === 'video') {
    return (
      <video controls src={url} style={{ maxWidth: 280, maxHeight: 280, borderRadius: 8 }}>
        seu navegador nao suporta video
      </video>
    );
  }
  // documento
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      download
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 10px', borderRadius: 6,
        background: 'rgba(255,255,255,0.12)', color: '#fff',
        textDecoration: 'none', fontSize: '0.85rem',
      }}
    >
      <i className="bi bi-file-earmark-arrow-down" />
      Baixar documento
    </a>
  );
}

function MensagemStatus({ status }) {
  if (!status || status === 'erro') return null;

  const lida = status === 'lida';
  const entregue = status === 'entregue';
  const icon = entregue || lida ? 'bi-check2-all' : 'bi-check2';
  const label = lida ? 'Lida' : entregue ? 'Entregue' : 'Enviada';

  return (
    <i
      className={`bi ${icon}`}
      title={label}
      aria-label={label}
      style={{
        color: lida ? '#22e3ff' : 'rgba(255,255,255,0.78)',
        fontSize: '1.05rem',
        lineHeight: 1,
      }}
    />
  );
}

function QuotedBlock({ mensagem, contatoNome, contatoNumero }) {
  const tipo = mensagem.quoted_tipo || 'texto';
  const iconeMap = {
    imagem: 'bi-image', audio: 'bi-mic-fill', video: 'bi-camera-video-fill',
    documento: 'bi-file-earmark', figurinha: 'bi-emoji-smile',
  };
  const icone = iconeMap[tipo];
  const preview = mensagem.quoted_conteudo
    ? String(mensagem.quoted_conteudo).slice(0, 120)
    : `[${tipo}]`;
  const remetente = mensagem.quoted_direcao === 'out'
    ? (mensagem.quoted_autor || 'atendente')
    : (contatoNome || (contatoNumero ? formatNumero(contatoNumero) : null) || 'cliente');
  return (
    <div style={{
      borderLeft: '3px solid rgba(255,255,255,0.45)',
      padding: '3px 8px',
      marginBottom: 6,
      borderRadius: '0 4px 4px 0',
      background: 'rgba(0,0,0,0.18)',
      fontSize: '0.78rem',
    }}>
      <div style={{ fontWeight: 700, opacity: 0.9, marginBottom: 1 }}>{remetente}</div>
      <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {icone && tipo !== 'texto' && <i className={`bi ${icone} me-1`} />}
        {preview}
      </div>
    </div>
  );
}

function LocalizacaoRenderer({ conteudo }) {
  let dados = null;
  try { dados = JSON.parse(conteudo || '{}'); } catch (_) { /* ignora */ }
  const lat = Number(dados?.latitude);
  const lng = Number(dados?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return <div style={{ fontStyle: 'italic', opacity: 0.85 }}>[localizacao invalida]</div>;
  }
  const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  const staticMap = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=15&size=280x140&markers=${lat},${lng},red-pushpin`;
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      style={{ display: 'block', color: '#fff', textDecoration: 'none' }}
    >
      <img
        src={staticMap}
        alt="Mapa"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ display: 'block', width: 280, maxWidth: '100%', borderRadius: 8, marginBottom: 4 }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="bi bi-geo-alt-fill" />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {dados?.name || 'Localizacao'}
          </div>
          {dados?.address && (
            <div style={{ fontSize: '0.75rem', opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {dados.address}
            </div>
          )}
        </div>
      </div>
    </a>
  );
}

function ContatoRenderer({ conteudo }) {
  let dados = null;
  try { dados = JSON.parse(conteudo || '{}'); } catch (_) { /* ignora */ }
  const lista = Array.isArray(dados?.contatos)
    ? dados.contatos
    : (dados?.displayName || dados?.vcard ? [dados] : []);
  if (!lista.length) return <div style={{ fontStyle: 'italic', opacity: 0.85 }}>[contato]</div>;
  return (
    <div>
      {lista.map((c, i) => {
        // Tenta extrair telefone do vCard se estiver la; senao mostra o displayName.
        const tel = c.phoneNumber || (c.vcard?.match(/TEL[^:]*:([\d+\-\s()]+)/i)?.[1] || null);
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: i === 0 ? 0 : '6px 0 0 0',
            borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.15)',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}><i className="bi bi-person-fill" /></div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{c.displayName || 'Contato'}</div>
              {tel && <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>{tel}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Tira curta de emojis rapidos (mesma pegada do WhatsApp). Se quiser outro,
// o usuario cai no menu ⋮ > "Reagir com outro..." (nao implementado ainda —
// se pedir depois adiciona).
const EMOJIS_REACAO_RAPIDA = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

function MessageBubble({ mensagem, contatoNome, contatoNumero, onAbrirImagem, onResponder, onEncaminhar, onReagir, onApagar, onEditar }) {
  const [hovered, setHovered] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [reacaoPickerAberto, setReacaoPickerAberto] = useState(false);

  // Mensagem interna (transferencia, etc): nota central, nao e balao de chat.
  if (mensagem.tipo === 'sistema') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
        <div style={{
          maxWidth: '85%', textAlign: 'center',
          background: 'rgba(240,173,78,0.12)',
          border: '1px solid rgba(240,173,78,0.35)',
          color: 'var(--portal-text)',
          padding: '6px 12px', borderRadius: 10,
          fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          <i className="bi bi-arrow-left-right me-1" style={{ color: '#f0ad4e' }} />
          {mensagem.conteudo}
        </div>
      </div>
    );
  }

  const out = mensagem.direcao === 'out';
  const erro = mensagem.status === 'erro';
  const bg = out
    ? (erro ? '#b43737' : 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)')
    : 'rgba(255, 255, 255, 0.06)';
  const align = out ? 'flex-end' : 'flex-start';
  const podeResponder = mensagem.tipo !== 'sistema' && onResponder;
  // Encaminhavel: qualquer mensagem exceto sistema/reacao — midia so se estiver
  // no storage interno (path relativo). URL http (WhatsApp) nao serve.
  const podeEncaminhar = onEncaminhar
    && mensagem.tipo !== 'sistema'
    && mensagem.tipo !== 'reacao'
    && !(mensagem.midia_url && /^https?:\/\//i.test(mensagem.midia_url));
  // Pode reagir quando temos evolution_id (mensagem entregue ao WhatsApp) e o
  // handler. Reagir na propria mensagem enviada tambem funciona no WhatsApp.
  const podeReagir = onReagir && mensagem.tipo !== 'sistema' && mensagem.tipo !== 'reacao' && mensagem.evolution_id;
  // Apagar/editar: so em msgs que ENVIAMOS, ainda nao apagadas, e com evolution_id.
  // Regra dos 2 dias/15min do WhatsApp fica com o backend/API — nao dupliquei aqui.
  const podeApagar = onApagar && out && mensagem.evolution_id && !mensagem.apagada && mensagem.tipo !== 'sistema';
  const podeEditar = onEditar && out && mensagem.evolution_id && !mensagem.apagada && mensagem.tipo === 'texto';
  const temMenu = !mensagem.apagada && (podeResponder || podeEncaminhar || podeReagir || podeApagar || podeEditar);

  const remetente = out
    ? (mensagem.autor || (mensagem.tipo === 'sistema' ? 'sistema' : null))
    : (contatoNome || formatNumero(contatoNumero) || 'cliente');

  const ehMidia = ['imagem', 'audio', 'video', 'documento', 'figurinha'].includes(mensagem.tipo);
  const conteudoTexto = mensagem.conteudo || null;

  return (
    <div
      style={{ display: 'flex', justifyContent: align, marginBottom: mensagem.reacao ? 14 : 6 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setMenuAberto(false); setReacaoPickerAberto(false); }}
    >
      <div
        style={{
          position: 'relative',
          maxWidth: '70%',
          background: bg,
          color: '#fff',
          padding: '8px 12px',
          borderRadius: 12,
          borderTopRightRadius: out ? 4 : 12,
          borderTopLeftRadius: out ? 12 : 4,
          fontSize: '0.9rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      >
        {temMenu && (hovered || menuAberto) && (
          <div style={{ position: 'absolute', top: 4, right: 6, zIndex: 10 }}>
            <button
              type="button"
              onClick={() => setMenuAberto((v) => !v)}
              title="Opcoes"
              style={{
                background: 'rgba(0,0,0,0.25)',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                borderRadius: 4,
                padding: '1px 5px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="bi bi-three-dots-vertical" style={{ fontSize: '0.85rem', fontWeight: 700 }} />
            </button>
            {menuAberto && (
              <div style={{
                position: 'absolute',
                top: '100%',
                // Mensagem `in` esta encostada na esquerda: o menu (150px)
                // abrindo pra esquerda transborda a <section> do chat, que
                // tem overflow:hidden, e fica escondido atras da lista. Pra
                // `in` abrimos pra direita; pra `out` continuamos pra esquerda.
                ...(out ? { right: 0 } : { left: 0 }),
                marginTop: 4,
                zIndex: 100,
                background: 'var(--portal-surface)',
                border: '1px solid var(--portal-border)',
                borderRadius: 8,
                minWidth: 150,
                boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                overflow: 'hidden',
              }}>
                {podeResponder && (
                  <button
                    type="button"
                    onClick={() => { onResponder(mensagem); setMenuAberto(false); setHovered(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--portal-text)', fontSize: '0.88rem', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-reply" />
                    Responder
                  </button>
                )}
                {podeEncaminhar && (
                  <button
                    type="button"
                    onClick={() => { onEncaminhar(mensagem); setMenuAberto(false); setHovered(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--portal-text)', fontSize: '0.88rem', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-arrow-90deg-right" />
                    Encaminhar
                  </button>
                )}
                {podeEditar && (
                  <button
                    type="button"
                    onClick={() => { onEditar(mensagem); setMenuAberto(false); setHovered(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--portal-text)', fontSize: '0.88rem', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-pencil" />
                    Editar
                  </button>
                )}
                {podeApagar && (
                  <button
                    type="button"
                    onClick={() => { onApagar(mensagem); setMenuAberto(false); setHovered(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '9px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#e57373', fontSize: '0.88rem', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-trash" />
                    Apagar pra todos
                  </button>
                )}
                {podeReagir && (
                  <div style={{ borderTop: '1px solid var(--portal-border)', padding: '6px 8px' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'space-between' }}>
                      {EMOJIS_REACAO_RAPIDA.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => {
                            // Toggle: reagir de novo com o mesmo emoji REMOVE.
                            const novo = mensagem.reacao === e ? '' : e;
                            onReagir(mensagem, novo);
                            setMenuAberto(false);
                            setHovered(false);
                          }}
                          title={mensagem.reacao === e ? 'Remover reacao' : `Reagir ${e}`}
                          style={{
                            background: mensagem.reacao === e ? 'rgba(42,138,156,0.22)' : 'transparent',
                            border: 'none', cursor: 'pointer', padding: '4px 6px',
                            borderRadius: '50%', fontSize: '1.15rem', lineHeight: 1,
                          }}
                        >{e}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {mensagem.quoted_id && (
          <QuotedBlock mensagem={mensagem} contatoNome={contatoNome} contatoNumero={contatoNumero} />
        )}
        {remetente && !erro && (
          <div style={{ fontSize: '0.7rem', opacity: 0.75, marginBottom: 2, fontWeight: 600 }}>
            {remetente}
          </div>
        )}
        {mensagem.apagada ? (
          <div style={{ fontStyle: 'italic', opacity: 0.75, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-slash-circle" />
            {out ? 'Voce apagou esta mensagem' : 'Esta mensagem foi apagada'}
          </div>
        ) : mensagem.tipo === 'localizacao' ? (
          <LocalizacaoRenderer conteudo={mensagem.conteudo} />
        ) : mensagem.tipo === 'contato' ? (
          <ContatoRenderer conteudo={mensagem.conteudo} />
        ) : (
          <>
            {ehMidia && <MidiaRenderer mensagem={mensagem} onAbrirImagem={onAbrirImagem} out={out} />}
            {ehMidia && conteudoTexto && (
              <div style={{ marginTop: 4 }}>{conteudoTexto}</div>
            )}
            {!ehMidia && (
              <div>{conteudoTexto || '[mensagem]'}</div>
            )}
          </>
        )}
        <div style={{
          fontSize: '0.7rem', opacity: 0.7, marginTop: 4, textAlign: 'right',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6,
        }}>
          {mensagem.chamado_id && (
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '0 6px', borderRadius: 8, fontWeight: 600,
            }}>
              #{mensagem.chamado_id}
            </span>
          )}
          {mensagem.editada_em && !mensagem.apagada && (
            <span style={{ fontStyle: 'italic', opacity: 0.75 }}>editada</span>
          )}
          <span>{formatHora(mensagem.criada_em)}{erro && ' - falha ao enviar'}</span>
          {out && !erro && !mensagem.apagada && <MensagemStatus status={mensagem.status} />}
        </div>
        {mensagem.reacao && (
          <div style={{
            position: 'absolute', bottom: -10, right: out ? 4 : undefined, left: out ? undefined : 4,
            background: 'var(--portal-surface)',
            border: '1px solid var(--portal-border)',
            borderRadius: 12, padding: '1px 6px',
            fontSize: '0.85rem', lineHeight: 1.3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          }}>
            {mensagem.reacao}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_CHAMADO_RESOLVIDO = new Set(['resolvido', 'fechado', 'cancelado']);

// Bolinha colorida de SLA: verde = folga, laranja = passou de 70% do limite,
// vermelho = estourou. Sem SLA configurado (ou sem prioridade) -> null.
// Texto na tag: tempo restante quando dentro do SLA, "+Xmin" quando estourou.
function SlaBolinha({ decorridoSegs, limiteMin, faseLabel }) {
  if (!limiteMin || decorridoSegs == null) return null;
  const limiteSegs = limiteMin * 60;
  const ratio = decorridoSegs / limiteSegs;
  let cor;
  let texto;
  if (ratio >= 1) {
    cor = '#dc3545';
    const estouroMin = Math.floor((decorridoSegs - limiteSegs) / 60);
    texto = `+${estouroMin}m`;
  } else if (ratio >= 0.7) {
    cor = '#fe8d25';
    const restanteMin = Math.ceil((limiteSegs - decorridoSegs) / 60);
    texto = `${restanteMin}m`;
  } else {
    cor = '#28a745';
    const restanteMin = Math.ceil((limiteSegs - decorridoSegs) / 60);
    texto = `${restanteMin}m`;
  }
  const titulo = ratio >= 1
    ? `SLA ${faseLabel} estourado em ${Math.floor((decorridoSegs - limiteSegs) / 60)}min uteis`
    : `SLA ${faseLabel}: ${Math.ceil((limiteSegs - decorridoSegs) / 60)}min uteis restantes (limite ${limiteMin}min)`;
  return (
    <span
      title={titulo}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.62rem', fontWeight: 700,
        background: `${cor}22`, color: cor,
        padding: '1px 6px', borderRadius: 8, border: `1px solid ${cor}55`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%', background: cor,
        display: 'inline-block',
      }} />
      {texto}
    </span>
  );
}

// Cronometro de SLA do chamado vinculado a conversa.
// Tres fases:
//   1) Chamado pendente de primeira resposta -> conta TTFR (icone stopwatch).
//   2) Atendente ja respondeu mas chamado nao foi resolvido -> conta TTR
//      (icone hourglass), continuando a partir do mesmo created_at.
//   3) Chamado resolvido/fechado/cancelado OU sem chamado vinculado -> nada.
// Em horario fora da janela o numero para de subir.
function CronometroSLA({
  chamadoCreatedAt, primeiraRespostaEm, chamadoStatus,
  agora, horarios, horarioAtivo,
  status, prioridade, slaTtfrMap, slaTtrMap,
  compact = false,
}) {
  if (!chamadoCreatedAt) return null;
  if (status === 'encerrada') return null;
  if (chamadoStatus && STATUS_CHAMADO_RESOLVIDO.has(chamadoStatus)) return null;

  const fase = primeiraRespostaEm ? 'ttr' : 'ttfr';
  const segs = segundosEmJanela(chamadoCreatedAt, agora, horarios, horarioAtivo);
  if (segs == null) return null;

  const correndo = dentroDaJanela(agora, horarios, horarioAtivo);
  const cor = !correndo ? '#5E7A82' : (segs >= 1800 ? '#fe8d25' : '#2A8A9C');
  const tituloBase = fase === 'ttfr'
    ? 'Aguardando primeira resposta'
    : 'Aguardando solucao do chamado';
  const titulo = correndo ? tituloBase : `${tituloBase} (fora do horario - cronometro pausado)`;

  const limite = (prioridade && (fase === 'ttfr' ? slaTtfrMap : slaTtrMap))
    ? (fase === 'ttfr' ? slaTtfrMap[prioridade] : slaTtrMap[prioridade])
    : null;
  const faseLabel = fase === 'ttfr' ? '1a resp.' : 'solucao';
  const icone = !correndo
    ? 'bi-pause-circle'
    : (fase === 'ttfr' ? 'bi-stopwatch' : 'bi-hourglass-split');

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        title={titulo}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: compact ? '0.65rem' : '0.72rem',
          fontWeight: 600,
          background: `${cor}22`, color: cor,
          padding: compact ? '1px 6px' : '2px 8px',
          borderRadius: 8, border: `1px solid ${cor}55`,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        <i className={`bi ${icone}`} />
        {formatarCronometro(segs)}
      </span>
      <SlaBolinha decorridoSegs={segs} limiteMin={limite} faseLabel={faseLabel} />
    </span>
  );
}

function ConversaItem({
  conversa, ativa, onClick, agora, horarios, horarioAtivo, slaTtfrMap, slaTtrMap,
}) {
  const cor = STATUS_COLOR[conversa.status] || '#5E7A82';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        width: '100%',
        textAlign: 'left',
        background: ativa ? 'rgba(42, 138, 156, 0.18)' : 'transparent',
        border: 'none',
        borderLeft: ativa ? '3px solid #2A8A9C' : '3px solid transparent',
        borderBottom: '1px solid var(--portal-border)',
        color: 'var(--portal-text)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 42, height: 42, borderRadius: '50%',
          background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 600, fontSize: '0.9rem', flexShrink: 0,
        }}
      >
        {iniciaisDe(conversa.push_name, conversa.numero)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <strong style={{
            fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {conversa.push_name || formatNumero(conversa.numero) || conversa.jid}
          </strong>
          <span style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', flexShrink: 0 }}>
            {formatHora(conversa.ultima_mensagem_em)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
          <span style={{
            fontSize: '0.78rem', color: 'var(--portal-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {conversa.ultima_preview || '(sem mensagens)'}
          </span>
          {conversa.nao_lidas > 0 && (
            <span style={{
              background: '#fe8d25', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
              padding: '1px 7px', borderRadius: 10, flexShrink: 0,
            }}>
              {conversa.nao_lidas}
            </span>
          )}
        </div>
        <div style={{
          marginTop: 4, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 6, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '0.65rem', fontWeight: 600,
              background: `${cor}22`, color: cor,
              padding: '1px 6px', borderRadius: 8, border: `1px solid ${cor}55`,
            }}>
              {STATUS_LABEL[conversa.status] || conversa.status}
            </span>
            {conversa.atendente && (
              <span
                title={`Em atendimento por ${conversa.atendente}`}
                style={{
                  fontSize: '0.65rem', fontWeight: 600,
                  background: 'rgba(42, 138, 156, 0.18)', color: '#2A8A9C',
                  padding: '1px 6px', borderRadius: 8,
                  border: '1px solid rgba(42, 138, 156, 0.45)',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                <i className="bi bi-person-check" />{conversa.atendente}
              </span>
            )}
          </span>
          <CronometroSLA
            chamadoCreatedAt={conversa.chamado_created_at}
            primeiraRespostaEm={conversa.primeira_resposta_em}
            chamadoStatus={conversa.chamado_status}
            agora={agora}
            horarios={horarios}
            horarioAtivo={horarioAtivo}
            status={conversa.status}
            prioridade={conversa.chamado_prioridade}
            slaTtfrMap={slaTtfrMap}
            slaTtrMap={slaTtrMap}
            compact
          />
        </div>
      </div>
    </button>
  );
}

function parseBotDados(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function EncerrarModal({ aberto, conversaAtiva, categorias, modos, chamadoEmFoco, chamadosVinculados, onClose, onConfirmar }) {
  const [modoId, setModoId] = useState('');
  const [criarChamado, setCriarChamado] = useState(false);
  const [resolverChamadoFoco, setResolverChamadoFoco] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('urgente');
  const [dataLimite, setDataLimite] = useState('');
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  const somenteResolverChamados = conversaAtiva?.status === 'encerrada';

  useEffect(() => {
    if (!aberto) return;
    const bd = parseBotDados(conversaAtiva?.bot_dados);
    setModoId(modos[0] ? String(modos[0].id) : '');
    setTitulo(bd?.titulo || '');
    setCategoria(bd?.categoria || categorias[0]?.chave || '');
    setPrioridade('urgente');
    setDataLimite('');
    setComentario('');
    setCriarChamado(false);
    setResolverChamadoFoco(!!chamadoEmFoco);
    setEnviando(false);
  }, [aberto]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!aberto) return null;

  const modoSelecionado = modos.find((m) => String(m.id) === String(modoId)) || null;
  const tituloOk = titulo.trim().length >= 3;
  const podeConfirmar = !enviando && Boolean(modoId) && (!criarChamado || tituloOk);

  const chamadoFocoInfo = chamadosVinculados?.find((c) => c.id === chamadoEmFoco) || null;

  const confirmar = async () => {
    if (!podeConfirmar) return;
    setEnviando(true);
    try {
      const payload = { modo_id: Number(modoId) };
      if (comentario.trim()) payload.comentario = comentario.trim();
      if (!somenteResolverChamados && criarChamado) {
        const bd = parseBotDados(conversaAtiva?.bot_dados);
        payload.chamado = {
          titulo: titulo.trim(),
          categoria: categoria || categorias[0]?.chave || 'outro',
          prioridade,
          descricao: bd?.descricao || comentario.trim() || titulo.trim(),
        };
        if (dataLimite) payload.chamado.data_limite = new Date(dataLimite).toISOString();
      }
      if (resolverChamadoFoco && chamadoEmFoco) {
        payload.chamado_em_foco = chamadoEmFoco;
      }
      await onConfirmar(payload);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
          <div className="modal-header" style={{ borderColor: 'var(--portal-border)' }}>
            <h5 className="modal-title">
              <i className="bi bi-check2-circle me-2" />
              Encerrar atendimento
            </h5>
            <button className="btn-close btn-close-white" onClick={onClose} disabled={enviando} />
          </div>

          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Modo de encerramento — obrigatorio */}
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                <i className="bi bi-flag me-1" /> Modo de encerramento <span style={{ color: '#dc3545' }}>*</span>
              </label>
              {modos.length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: '#fe8d25' }}>
                  Nenhum modo cadastrado. Peca a um administrador para cadastrar em /admin/atendimento.
                </div>
              ) : (
                <>
                  <select
                    className="form-select"
                    value={modoId}
                    onChange={(e) => setModoId(e.target.value)}
                  >
                    {modos.map((m) => (
                      <option key={m.id} value={String(m.id)}>{m.descricao}</option>
                    ))}
                  </select>
                  {modoSelecionado?.mensagem_cliente && (
                    <div style={{
                      marginTop: 8, padding: '8px 10px', borderRadius: 6,
                      background: 'rgba(42,138,156,0.08)', border: '1px solid rgba(42,138,156,0.2)',
                      fontSize: '0.78rem', color: 'var(--portal-muted)',
                    }}>
                      <i className="bi bi-chat-dots me-1" />
                      <em>{modoSelecionado.mensagem_cliente}</em>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Comentario */}
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 6 }}>
                <i className="bi bi-pencil-square me-1" /> Comentario do atendente (opcional)
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="O que foi feito, observacoes, proximos passos..."
              />
            </div>

            {/* Toggle Resolver chamado em foco */}
            {chamadoEmFoco && (
              <div style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 14 }}>
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="resolver-chamado-toggle"
                    checked={resolverChamadoFoco}
                    onChange={(e) => setResolverChamadoFoco(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="resolver-chamado-toggle" style={{ fontWeight: 600 }}>
                    Resolver chamado #{chamadoEmFoco}
                    {chamadoFocoInfo && (
                      <span style={{ fontWeight: 400, color: 'var(--portal-muted)', marginLeft: 6 }}>
                        — {chamadoFocoInfo.titulo}
                      </span>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Toggle Registrar Chamado (oculto quando conversa ja esta encerrada) */}
            {!somenteResolverChamados && (
            <div style={{ borderTop: '1px solid var(--portal-border)', paddingTop: 14 }}>
              <div className="form-check form-switch" style={{ marginBottom: criarChamado ? 14 : 0 }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="criar-chamado-toggle"
                  checked={criarChamado}
                  onChange={(e) => setCriarChamado(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="criar-chamado-toggle" style={{ fontWeight: 600 }}>
                  Registrar chamado
                </label>
              </div>

              {criarChamado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                      Titulo <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={titulo}
                      onChange={(e) => setTitulo(e.target.value)}
                      placeholder="Ex: Impressora nao imprime"
                      autoFocus
                    />
                  </div>
                  <div className="row g-2">
                    <div className="col-7">
                      <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                        Categoria
                      </label>
                      <select
                        className="form-select"
                        value={categoria}
                        onChange={(e) => setCategoria(e.target.value)}
                      >
                        {categorias.map((c) => (
                          <option key={c.chave} value={c.chave}>{c.nome}</option>
                        ))}
                        {categorias.length === 0 && <option value="outro">Outro</option>}
                      </select>
                    </div>
                    <div className="col-5">
                      <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                        Prioridade
                      </label>
                      <select
                        className="form-select"
                        value={prioridade}
                        onChange={(e) => setPrioridade(e.target.value)}
                      >
                        <option value="baixa">Baixa</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.85rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                      <i className="bi bi-alarm me-1" /> Data limite de entrega (opcional)
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={dataLimite}
                      onChange={(e) => setDataLimite(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          <div className="modal-footer" style={{ borderColor: 'var(--portal-border)' }}>
            <button className="btn btn-outline-secondary" onClick={onClose} disabled={enviando}>
              Cancelar
            </button>
            <button
              className="btn"
              onClick={confirmar}
              disabled={!podeConfirmar}
              style={{
                background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                color: '#fff', fontWeight: 600, border: 'none', padding: '6px 18px',
              }}
            >
              {enviando ? 'Encerrando...' : 'Encerrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Lightbox simples para visualizar imagens das conversas sem abrir nova
// aba do navegador. ESC ou botao fechar encerram. Usa portal-surface escura
// pra contraste e mantem aspect ratio da imagem.
const ZOOM_STEP = 0.2;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 5;

function LightboxImagem({ url, onClose }) {
  const [rotacao, setRotacao] = useState(0);
  const [zoom, setZoom] = useState(1);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  const ajustarZoom = (delta) =>
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 10) / 10)));

  useEffect(() => {
    if (!url) return undefined;
    setRotacao(0);
    setZoom(1);
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key === 'r' || e.key === 'R') setRotacao((r) => (r + 90) % 360);
      if (e.key === '+' || e.key === '=') ajustarZoom(ZOOM_STEP);
      if (e.key === '-') ajustarZoom(-ZOOM_STEP);
      if (e.key === '0') setZoom(1);
    };
    const onWheel = (e) => {
      e.preventDefault();
      ajustarZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
    };
  }, [url]);

  if (!url) return null;

  const btnStyle = {
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: '#fff', width: 36, height: 36, borderRadius: '50%',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontSize: '1.1rem',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        overflow: 'hidden',
      }}
      onClick={onClose}
    >
      {/* linha de botões */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        title="Fechar (ESC)"
        aria-label="Fechar"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 16 }}
      >
        <i className="bi bi-x-lg" />
      </button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        download
        title="Abrir em nova aba / baixar"
        onClick={(e) => e.stopPropagation()}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 64, textDecoration: 'none' }}
      >
        <i className="bi bi-box-arrow-up-right" />
      </a>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setRotacao((r) => (r + 90) % 360); }}
        title="Girar 90° (R)"
        aria-label="Girar imagem"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 112 }}
      >
        <i className="bi bi-arrow-clockwise" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ajustarZoom(ZOOM_STEP); }}
        title="Ampliar (+)"
        aria-label="Ampliar"
        disabled={zoom >= ZOOM_MAX}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 160, opacity: zoom >= ZOOM_MAX ? 0.4 : 1 }}
      >
        <i className="bi bi-zoom-in" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ajustarZoom(-ZOOM_STEP); }}
        title="Reduzir (-)"
        aria-label="Reduzir"
        disabled={zoom <= ZOOM_MIN}
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 208, opacity: zoom <= ZOOM_MIN ? 0.4 : 1 }}
      >
        <i className="bi bi-zoom-out" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setZoom(1); }}
        title="Zoom original (0)"
        aria-label="Zoom original"
        style={{ ...btnStyle, position: 'absolute', top: 16, right: 256, fontSize: '0.7rem', fontWeight: 700, opacity: zoom === 1 ? 0.4 : 1 }}
      >
        1:1
      </button>

      {/* indicador de zoom */}
      {zoom !== 1 && (
        <div style={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 12,
          padding: '3px 12px', fontSize: '0.78rem', pointerEvents: 'none',
        }}>
          {Math.round(zoom * 100)}%
        </div>
      )}

      <img
        src={url}
        alt="imagem ampliada"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain',
          borderRadius: 8, boxShadow: '0 6px 32px rgba(0,0,0,0.5)',
          cursor: zoom > 1 ? 'grab' : 'default',
          transform: `rotate(${rotacao}deg) scale(${zoom})`,
          transition: 'transform 0.2s ease',
          transformOrigin: 'center center',
        }}
      />
    </div>
  );
}

// Modal pra atendente iniciar uma conversa "outbound": busca contato existente
// ou cadastra um novo na hora, escreve a primeira mensagem e dispara via
// POST /atendimentos/iniciar. A conversa nasce em_atendimento com o atendente
// vinculado e bot calado - quando o cliente responder o bot nao mostra menu.
function NovaConversaModal({ aberto, onClose, onCriada }) {
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState([]);
  const [loadingContatos, setLoadingContatos] = useState(false);
  const [contatoSel, setContatoSel] = useState(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Cadastro inline de novo contato
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [canais, setCanais] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [setores, setSetores] = useState([]);
  const [novoForm, setNovoForm] = useState({ canal_id: '', numero: '', push_name: '', filial: '', setor: '' });
  const { user } = useAuth();
  const meuCanal = user?.canal_id ?? null;
  // Canais em que o usuario pode AGIR (= onde pode iniciar conversa outbound):
  // padrao + extras se assumir_outros. Ver [[multicanal-atendimento]].
  const canaisAtuar = (() => {
    const padrao = meuCanal != null ? Number(meuCanal) : null;
    const temNovo = Array.isArray(user?.canais_extra);
    const extras = temNovo ? user.canais_extra.map(Number).filter(Boolean) : [];
    const assumir = !!user?.assumir_outros;
    const dedupe = (a) => [...new Set(a.filter((v) => v != null))];
    return padrao != null ? dedupe([padrao, ...(assumir ? extras : [])]) : [];
  })();
  const multiCanalAtuar = canaisAtuar.length > 1;

  useEffect(() => {
    if (!aberto) return;
    setBusca(''); setContatoSel(null); setTexto(''); setErro('');
    setCriandoNovo(false);
    setNovoForm({ canal_id: '', numero: '', push_name: '', filial: '', setor: '' });
    (async () => {
      setLoadingContatos(true);
      try {
        const [r1, r2, r3, r4] = await Promise.all([
          axios.get(`${API}/contatos`),
          axios.get(`${API}/atendimento/canais`).catch(() => ({ data: { data: [] } })),
          axios.get(`${API}/atendimento/filiais`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
          axios.get(`${API}/atendimento/setores`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
        ]);
        setContatos(r1.data?.data || []);
        const cs = r2.data?.data || [];
        setCanais(cs);
        setFiliais(r3.data?.data || []);
        setSetores(r4.data?.data || []);
        setNovoForm((f) => ({ ...f, canal_id: meuCanal || canaisAtuar[0] || cs[0]?.id || '' }));
      } catch (e) {
        setErro('Nao foi possivel carregar contatos.');
      } finally {
        setLoadingContatos(false);
      }
    })();
  }, [aberto]);

  // Busca client-side
  const filtrados = useMemo(() => {
    if (!busca) return contatos;
    const q = busca.toLowerCase();
    return contatos.filter((c) =>
      String(c.push_name || '').toLowerCase().includes(q)
      || String(c.numero || '').includes(q),
    );
  }, [contatos, busca]);

  const cadastrarENovo = async () => {
    if (!novoForm.canal_id || !novoForm.numero) {
      setErro('Canal e numero sao obrigatorios.'); return;
    }
    if (!novoForm.push_name?.trim()) { setErro('Nome e obrigatorio.'); return; }
    try {
      const res = await axios.post(`${API}/contatos`, {
        canal_id: Number(novoForm.canal_id),
        numero: novoForm.numero,
        push_name: novoForm.push_name.trim(),
        filial: novoForm.filial.trim() || null,
        setor: novoForm.setor.trim() || null,
      });
      const novo = res.data?.data;
      if (novo) {
        setContatos((prev) => [novo, ...prev.filter((c) => c.id !== novo.id)]);
        setContatoSel(novo);
        setCriandoNovo(false);
      }
    } catch (e) {
      setErro(e.response?.data?.message || 'Falha ao cadastrar contato.');
    }
  };

  const enviar = async () => {
    if (!contatoSel) { setErro('Selecione um contato.'); return; }
    if (!texto.trim()) { setErro('Escreva a mensagem inicial.'); return; }
    setEnviando(true); setErro('');
    try {
      const res = await axios.post(`${API}/atendimentos/iniciar`, {
        contato_id: contatoSel.id,
        texto: texto.trim(),
      });
      onCriada?.(res.data?.data);
    } catch (e) {
      setErro(e.response?.data?.message || 'Falha ao iniciar conversa.');
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(8, 23, 43, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        className="content-card"
        style={{
          width: 'min(560px, 96vw)', padding: 0, display: 'flex',
          flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden',
        }}
      >
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
        }}>
          <strong>
            <i className="bi bi-plus-circle me-2" /> Nova conversa
          </strong>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose} disabled={enviando}
          >
            <i className="bi bi-x-lg" />
          </button>
        </header>

        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {erro && (
            <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
              {erro}
            </div>
          )}

          {!criandoNovo ? (
            <>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Contato</label>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Buscar por nome ou numero..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  autoFocus
                />
                <div style={{
                  marginTop: 6, maxHeight: 180, overflowY: 'auto',
                  border: '1px solid var(--portal-border)', borderRadius: 6,
                  background: 'var(--portal-surface)',
                }}>
                  {loadingContatos ? (
                    <div style={{ padding: 12, textAlign: 'center' }}>
                      <div className="screen-loader__spinner" />
                    </div>
                  ) : filtrados.length === 0 ? (
                    <div style={{ padding: 12, textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                      Nenhum contato. Cadastre um novo abaixo.
                    </div>
                  ) : filtrados.map((c) => {
                    const ativo = contatoSel?.id === c.id;
                    // Bot so faz intake quando falta NOME; filial/setor sao
                    // preenchidos pelo atendente e nao disparam o alerta.
                    const semNome = !c.push_name;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setContatoSel(c)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '8px 12px',
                          background: ativo ? 'rgba(42, 138, 156, 0.18)' : 'transparent',
                          border: 'none', borderBottom: '1px solid var(--portal-border)',
                          color: 'var(--portal-text)', cursor: 'pointer', fontSize: '0.85rem',
                        }}
                      >
                        <div>
                          <strong>{c.push_name || '(sem nome)'}</strong>
                          {semNome && (
                            <i
                              className="bi bi-exclamation-triangle ms-2"
                              title="Contato sem nome - bot fara a identificacao na proxima conversa"
                              style={{ color: '#fe8d25' }}
                            />
                          )}
                          <span style={{ marginLeft: 8, color: 'var(--portal-muted)' }}>
                            {c.numero}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
                          {[c.filial, c.setor].filter(Boolean).join(' · ') || 'sem filial/setor'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-info mt-2"
                  onClick={() => setCriandoNovo(true)}
                >
                  <i className="bi bi-plus-lg me-1" /> Cadastrar novo contato
                </button>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Mensagem inicial
                </label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Digite a primeira mensagem que o contato vai receber..."
                />
              </div>
            </>
          ) : (
            <div style={{
              padding: 12, borderRadius: 8,
              border: '1px solid var(--portal-border)',
              background: 'rgba(42, 138, 156, 0.08)',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <strong style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-person-plus me-2" /> Cadastrar novo contato
              </strong>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Canal</label>
                {multiCanalAtuar ? (
                  <select
                    className="form-select"
                    value={novoForm.canal_id}
                    onChange={(e) => setNovoForm({ ...novoForm, canal_id: e.target.value })}
                  >
                    {canais.filter((c) => canaisAtuar.includes(c.id)).map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                ) : (
                  <div className="form-control" style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--portal-muted)' }}>
                    <i className="bi bi-whatsapp me-2" style={{ color: '#25D366' }} />
                    {canais.find((c) => c.id === Number(novoForm.canal_id || meuCanal))?.nome || 'Seu canal'}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Numero (com DDD)
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={novoForm.numero}
                  onChange={(e) => setNovoForm({ ...novoForm, numero: e.target.value })}
                  placeholder="Ex: 21965868915"
                />
              </div>
              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={novoForm.push_name}
                  onChange={(e) => setNovoForm({ ...novoForm, push_name: e.target.value })}
                />
              </div>
              <div className="row g-2" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Filial (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={novoForm.filial}
                    onChange={(e) => setNovoForm({ ...novoForm, filial: e.target.value })}
                  >
                    <option value="">Nao informar</option>
                    {filiais.map((f) => (
                      <option key={f.id || f.nome} value={f.nome}>
                        {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Setor (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={novoForm.setor}
                    onChange={(e) => setNovoForm({ ...novoForm, setor: e.target.value })}
                  >
                    <option value="">Nao informar</option>
                    {setores.map((s) => (
                      <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setCriandoNovo(false)}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={cadastrarENovo}
                  style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
                >
                  <i className="bi bi-check2 me-1" /> Cadastrar e selecionar
                </button>
              </div>
            </div>
          )}
        </div>

        <footer style={{
          padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
            {contatoSel
              ? <><i className="bi bi-person-check" /> Selecionado: <strong>{contatoSel.push_name || contatoSel.numero}</strong></>
              : 'Selecione um contato'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={enviar}
              disabled={enviando || !contatoSel || !texto.trim()}
              style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
            >
              <i className="bi bi-send me-1" />
              {enviando ? 'Enviando...' : 'Iniciar conversa'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function MultiSelectDropdown({ label, options, selected, onChange, disabled }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [aberto]);

  const ativo = selected.length > 0;
  const btnLabel = ativo ? `${label} (${selected.length})` : label;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setAberto((v) => !v)}
        style={{
          fontSize: '0.7rem',
          padding: '4px 10px',
          borderRadius: 6,
          border: `1px solid ${ativo ? '#2A8A9C' : 'var(--portal-border)'}`,
          background: ativo ? 'rgba(42,138,156,0.18)' : 'transparent',
          color: ativo ? '#2A8A9C' : 'var(--portal-text)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          whiteSpace: 'nowrap',
          fontWeight: ativo ? 600 : 400,
        }}
      >
        {btnLabel}
        <i className={`bi bi-chevron-${aberto ? 'up' : 'down'}`} style={{ fontSize: '0.6rem' }} />
      </button>
      {aberto && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 100,
          width: 220,
          background: 'var(--portal-surface)',
          border: '1px solid var(--portal-border)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {options.length === 0 ? (
              <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: 'var(--portal-muted)' }}>
                Nenhuma opcao disponivel
              </div>
            ) : options.map((opt) => {
              const marcado = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    background: 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--portal-border)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => {
                      if (marcado) onChange(selected.filter((v) => v !== opt.value));
                      else onChange([...selected, opt.value]);
                    }}
                    style={{ accentColor: '#2A8A9C', cursor: 'pointer' }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ borderTop: '1px solid var(--portal-border)', padding: '4px 8px' }}>
              <button
                type="button"
                onClick={() => { onChange([]); setAberto(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: 'var(--portal-muted)',
                  padding: '2px 4px',
                }}
              >
                Limpar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Atendimentos() {
  const { user, temPermissao, refreshSession } = useAuth();
  const podeUsar = temPermissao('ti:atendimentos');

  // Ao abrir a tela, reemite o token com o escopo de canais atual do banco.
  // Garante que mudancas de canal/assumir_outros valham sem precisar relogar.
  useEffect(() => {
    refreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tela estreita OU tablet de toque usam layout do WhatsApp: uma coisa por
  // vez, lista OU conversa. Portado do fio-chatbot: `pointer: coarse` casa
  // celular/tablet (dedo como ponteiro primario); notebook com touch continua
  // `fine`. O teto de 1400 cobre tablet deitado sem alcancar monitor.
  const [larguraTela, setLarguraTela] = useState(
    () => (typeof window === 'undefined' ? 1440 : window.innerWidth),
  );
  useEffect(() => {
    const onResize = () => setLarguraTela(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const [ponteiroToque] = useState(() => (
    typeof window !== 'undefined'
    && !!window.matchMedia?.('(pointer: coarse)').matches
  ));
  const isMobile = larguraTela <= 768 || (ponteiroToque && larguraTela <= 1400);
  // Lembra o filtro de canal escolhido pelo supervisor (por usuario).
  const filtroCanalKey = `ti_atend_canais_filtro_${user?.id || 'anon'}`;

  const [conversas, setConversas] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [erro, setErro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [busca, setBusca] = useState('');
  // Janela de atendimento + relogio para o cronometro de TTFR ao vivo.
  // O backend manda os horarios junto com a lista (meta.horarios). O `agora`
  // tica a cada 1s para o cronometro renderizar suavemente.
  const [horarios, setHorarios] = useState([]);
  const [horarioAtivo, setHorarioAtivo] = useState(false);
  // SLA TTFR (1a resposta) e TTR (solucao) por prioridade vem do meta da
  // listagem. Cronometro usa o TTFR enquanto nao houve primeira resposta;
  // depois muda pra TTR ate o chamado ser resolvido.
  const [slaTtfrMap, setSlaTtfrMap] = useState({});
  const [slaTtrMap, setSlaTtrMap] = useState({});
  const [somNovaMensagem, setSomNovaMensagem] = useState({ ativo: true, tipo: 'suave', volume: 60 });
  const [agora, setAgora] = useState(() => new Date());
  // Filtro por atendente (multi-select). [] = todos.
  const [atendentesFiltro, setAtendentesFiltro] = useState([]);
  // Filtro por canal (multi-select, enviado ao backend via canal_ids). [] = todos.
  // Inicializa com a ultima escolha salva do usuario (supervisor).
  const [canaisFiltro, setCanaisFiltro] = useState(() => {
    try {
      const raw = localStorage.getItem(filtroCanalKey);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(Number).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [canais, setCanais] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [conversaAtivaId, setConversaAtivaId] = useState(null);
  const [conversaAtiva, setConversaAtiva] = useState(null);

  // Conversa imersiva no mobile: com uma conversa aberta, esconde a barra do
  // topo e vai full-bleed (regras em index.css). Precisa vir DEPOIS de
  // `conversaAtivaId` — senao TDZ e tela preta.
  useEffect(() => {
    const imersivo = isMobile && !!conversaAtivaId;
    document.body.classList.toggle('conversa-imersiva', imersivo);
    return () => document.body.classList.remove('conversa-imersiva');
  }, [isMobile, conversaAtivaId]);

  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [chamadosVinculados, setChamadosVinculados] = useState([]);
  const [chamadoEmFoco, setChamadoEmFoco] = useState(null);
  const [encerrarAberto, setEncerrarAberto] = useState(false);
  const [categoriasEncerrar, setCategoriasEncerrar] = useState([]);
  const [modosEncerrar, setModosEncerrar] = useState([]);
  const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
  const inputArquivoRef = useRef(null);
  const [gravando, setGravando] = useState(false);
  const [tempoGravacao, setTempoGravacao] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerGravacaoRef = useRef(null);
  const canceladoRef = useRef(false);
  const conversaParaEnvioRef = useRef(null);
  // URL da imagem atualmente aberta no lightbox; null = fechado.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [novaConversaAberta, setNovaConversaAberta] = useState(false);
  const [contatoSidebarAberta, setContatoSidebarAberta] = useState(false);
  const [contatoSidebarForm, setContatoSidebarForm] = useState({ push_name: '', filial: '', setor: '' });
  const [contatoSidebarFiliais, setContatoSidebarFiliais] = useState([]);
  const [contatoSidebarSetores, setContatoSidebarSetores] = useState([]);
  const [contatoSidebarSalvando, setContatoSidebarSalvando] = useState(false);
  const [contatoSidebarErro, setContatoSidebarErro] = useState('');
  // Busca na conversa: barra abre com Ctrl+F ou lupa no header. `hits` sao os
  // ids das mensagens que casam; `hitAtivo` e o indice atual. Recalcula em
  // memoria, sem query — mensagens ja estao carregadas.
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [buscaTermo, setBuscaTermo] = useState('');
  const [buscaHitAtivo, setBuscaHitAtivo] = useState(0);
  const buscaInputRef = useRef(null);
  // Encaminhar mensagem: guarda a msg escolhida no menu; modal mostra a lista
  // de conversas que o usuario ve (reusa `conversas`) pra escolher destino.
  const [encaminharMsg, setEncaminharMsg] = useState(null);
  const [encaminharBusca, setEncaminharBusca] = useState('');
  const [encaminhando, setEncaminhando] = useState(false);
  // Modal enviar localizacao/contato.
  const [locModalAberto, setLocModalAberto] = useState(false);
  const [locBuscandoGps, setLocBuscandoGps] = useState(false);
  const [locForm, setLocForm] = useState({ latitude: '', longitude: '', name: '', address: '' });
  const [contatoModalAberto, setContatoModalAberto] = useState(false);
  const [contatoBuscaAg, setContatoBuscaAg] = useState('');
  const [contatoAgResultados, setContatoAgResultados] = useState([]);
  const [contatoManual, setContatoManual] = useState({ fullName: '', phoneNumber: '' });
  // Presenca do cliente: 'digitando' | 'gravando' | null. Alimentado pelo SSE
  // (webhook presence.update). Auto-limpa em 6s (o WhatsApp ja para de mandar
  // presence quando o cliente para; a Evolution repassa isso mas nem sempre —
  // guarda-chuva local).
  const [presencaCliente, setPresencaCliente] = useState(null);
  const presencaTimerRef = useRef(null);
  // Debounce pra enviar "composing" do atendente sem inundar a Evolution.
  const atendenteTypingTimerRef = useRef(null);
  const atendenteTypingUltimoEnvioRef = useRef(0);
  // Respostas rapidas cadastradas em /admin/atendimento. Carregadas uma vez
  // ao entrar na tela; sao poucas dezenas, entao fica em memoria.
  const [respostasRapidas, setRespostasRapidas] = useState([]);
  // Sugestoes que casam com o que ta sendo digitado apos "/". Null = fechado.
  const [respostaSugestoes, setRespostaSugestoes] = useState(null);
  // ⚡ botao: quando aberto, mostra todas as respostas ativas.
  const [respostaPickerAberto, setRespostaPickerAberto] = useState(false);

  const textareaRef = useRef(null);
  const pollListaRef = useRef(null);
  const pollDetalheRef = useRef(null);
  const mensagensRef = useRef(null);
  const totalNaoLidasRef = useRef(null);
  const somNovaMensagemRef = useRef(somNovaMensagem);
  // Refs para o canal SSE acessar sempre o estado/funcoes mais recentes sem
  // reconectar a cada render ou troca de conversa.
  const conversaAtivaIdRef = useRef(null);
  const carregarDetalheRef = useRef(() => {});
  const carregarListaRef = useRef(() => {});
  const listaDebounceRef = useRef(null);
  // Deep-link "abrir conversa de origem" do chamado: quando setado, o proximo
  // carregamento de mensagens reposiciona a thread em vez de ir pro fim.
  // marcoOrigemRef guarda o instante em que o chamado nasceu (created_at);
  // posicionamos na mensagem mais proxima desse instante. Sem marco, rola pro topo.
  const rolarParaInicioRef = useRef(false);
  const marcoOrigemRef = useRef(null);

  useEffect(() => {
    somNovaMensagemRef.current = somNovaMensagem;
  }, [somNovaMensagem]);

  const carregarLista = async (mostrarLoading = false) => {
    if (mostrarLoading) setLoadingLista(true);
    try {
      const params = {};
      if (statusFiltro) params.status = statusFiltro;
      if (busca) params.busca = busca;
      if (canaisFiltro.length > 0) params.canal_ids = canaisFiltro.join(',');
      if (atendentesFiltro.length > 0 && temPermissao('ti:atendimentos:todos')) params.atendentes = atendentesFiltro.join(',');
      const res = await axios.get(`${API}/atendimentos`, { params });
      const lista = res.data?.data || [];
      setConversas(lista);
      const meta = res.data?.meta;
      if (meta) {
        setHorarios(Array.isArray(meta.horarios) ? meta.horarios : []);
        setHorarioAtivo(Boolean(meta.horario_ativo));
        setSlaTtfrMap(meta.sla_ttfr_min || {});
        setSlaTtrMap(meta.sla_ttr_min || {});
        if (meta.som_nova_mensagem) {
          setSomNovaMensagem({
            ativo: meta.som_nova_mensagem.ativo !== false,
            tipo: meta.som_nova_mensagem.tipo || 'suave',
            volume: Number(meta.som_nova_mensagem.volume ?? 60),
          });
        }
      }

      const totalNaoLidas = lista.reduce((total, c) => total + (Number(c.nao_lidas) || 0), 0);
      const totalAnterior = totalNaoLidasRef.current;
      if (totalAnterior !== null && totalNaoLidas > totalAnterior) {
        playNotificationSound(somNovaMensagemRef.current).catch(() => {});
      }
      totalNaoLidasRef.current = totalNaoLidas;
      setErro('');
    } catch (err) {
      setErro('Nao foi possivel carregar as conversas.');
    } finally {
      if (mostrarLoading) setLoadingLista(false);
    }
  };

  const carregarDetalhe = async (id, mostrarLoading = false) => {
    if (!id) return;
    if (mostrarLoading) setLoadingDetalhe(true);
    try {
      const res = await axios.get(`${API}/atendimentos/${id}`);
      const novo = res.data?.data || null;
      // Preserva midia_signed_url ja conhecida por mensagem entre polls. O
      // backend regenera a signed URL a cada GET, e se trocarmos o `src` do
      // <video>/<audio> em pleno polling de 4s o player reinicia toda vez.
      // Mantendo a mesma URL, o React nao toca em src e a reproducao segue.
      if (novo?.mensagens) {
        setConversaAtiva((prev) => {
          const prevMsgs = prev?.id === novo.id ? (prev.mensagens || []) : [];
          const prevById = new Map(prevMsgs.map((m) => [m.id, m]));
          const mensagens = novo.mensagens.map((m) => {
            const old = prevById.get(m.id);
            return old?.midia_signed_url
              ? { ...m, midia_signed_url: old.midia_signed_url }
              : m;
          });
          return { ...novo, mensagens };
        });
      } else {
        setConversaAtiva(novo);
      }
      // Chegaram mensagens nao lidas enquanto o chat estava aberto — zera silenciosamente.
      if (!mostrarLoading && novo?.nao_lidas > 0) {
        axios.post(`${API}/atendimentos/${id}/lida`).catch(() => {});
      }
    } catch (err) {
      setConversaAtiva(null);
    } finally {
      if (mostrarLoading) setLoadingDetalhe(false);
    }
  };

  const carregarChamadosVinculados = async (id) => {
    if (!id) {
      setChamadosVinculados([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/atendimentos/${id}/chamados-vinculados`);
      const lista = res.data?.data || [];
      setChamadosVinculados(lista);
      // Auto-foca: se conversa ja tem chamado_id e ele esta na lista, foca nele.
      // Senao, pega o primeiro da lista.
      setChamadoEmFoco((prev) => {
        if (lista.some((c) => c.id === prev)) return prev;
        return lista[0]?.id || null;
      });
    } catch (e) {
      setChamadosVinculados([]);
    }
  };

  // Carga inicial e quando os filtros mudam
  useEffect(() => {
    if (!podeUsar) {
      setLoadingLista(false);
      return;
    }
    totalNaoLidasRef.current = null;
    carregarLista(true);
  }, [podeUsar, statusFiltro, busca, canaisFiltro, atendentesFiltro]);

  useEffect(() => {
    if (!podeUsar) return undefined;
    const desbloquear = () => {
      unlockNotificationSound().catch(() => {});
    };
    window.addEventListener('pointerdown', desbloquear, { once: true });
    window.addEventListener('keydown', desbloquear, { once: true });
    return () => {
      window.removeEventListener('pointerdown', desbloquear);
      window.removeEventListener('keydown', desbloquear);
    };
  }, [podeUsar]);

  // Carrega respostas rapidas do atendente (uma vez por sessao). Sao poucas.
  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/atendimento/respostas-rapidas`, { params: { ativas: 'true' } })
      .then((res) => setRespostasRapidas(res.data?.data || []))
      .catch(() => setRespostasRapidas([]));
  }, [podeUsar]);

  // Carrega lista de canais para o filtro (uma vez por sessao).
  useEffect(() => {
    if (!podeUsar) return;
    axios.get(`${API}/atendimento/canais`)
      .then((res) => {
        const lista = res.data?.data || [];
        setCanais(lista);
        // Saneamento: descarta do filtro qualquer canal que nao existe mais
        // (ex.: canal excluido). Sem isso, um id morto preso no localStorage
        // faz o backend intersectar para vazio e a lista some por completo.
        const idsValidos = new Set(lista.map((c) => Number(c.id)));
        setCanaisFiltro((prev) => {
          const limpo = prev.filter((id) => idsValidos.has(Number(id)));
          return limpo.length === prev.length ? prev : limpo;
        });
      })
      .catch(() => {});
  }, [podeUsar]);

  // Persiste o filtro de canal escolhido para lembrar entre sessoes.
  useEffect(() => {
    try {
      localStorage.setItem(filtroCanalKey, JSON.stringify(canaisFiltro));
    } catch {
      /* localStorage indisponivel - ignora */
    }
  }, [canaisFiltro, filtroCanalKey]);

  // Polling da lista a cada 5s
  useEffect(() => {
    if (!podeUsar) return undefined;
    pollListaRef.current = setInterval(() => carregarLista(false), 5000);
    return () => {
      if (pollListaRef.current) clearInterval(pollListaRef.current);
      pollListaRef.current = null;
    };
  }, [podeUsar, statusFiltro, busca, canaisFiltro, atendentesFiltro]);

  // Tick de 1s para o cronometro de TTFR. Sem efeito quando ninguem esta
  // aguardando (CronometroTTFR retorna null) - so causa re-render barato.
  useEffect(() => {
    if (!podeUsar) return undefined;
    const t = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(t);
  }, [podeUsar]);

  // Deep-link "abrir conversa de origem" do chamado: ?conversa=ID seleciona a
  // conversa e marca pra rolar ate o inicio da thread. Consome o parametro da
  // URL apos aplicar pra nao reabrir ao trocar de conversa manualmente.
  useEffect(() => {
    const alvo = Number(searchParams.get('conversa'));
    if (!alvo) return;
    marcoOrigemRef.current = searchParams.get('marco') || null;
    rolarParaInicioRef.current = true;
    setConversaAtivaId(alvo);
    const restante = new URLSearchParams(searchParams);
    restante.delete('conversa');
    restante.delete('marco');
    setSearchParams(restante, { replace: true });
  }, [searchParams, setSearchParams]);

  // Carregar detalhe, chamados vinculados e marcar como lida ao selecionar
  useEffect(() => {
    setContatoSidebarAberta(false);
    setContatoSidebarForm({ push_name: '', filial: '', setor: '' });
    setReplyTo(null);
    if (!conversaAtivaId) {
      setConversaAtiva(null);
      setChamadosVinculados([]);
      setChamadoEmFoco(null);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      await carregarDetalhe(conversaAtivaId, true);
      if (cancelado) return;
      await carregarChamadosVinculados(conversaAtivaId);
      if (cancelado) return;
      try {
        await axios.post(`${API}/atendimentos/${conversaAtivaId}/lida`);
        await carregarLista(false);
      } catch (e) { /* nao bloqueia */ }
    })();
    return () => { cancelado = true; };
  }, [conversaAtivaId]);

  // Polling do detalhe a cada 4s (fallback caso o SSE caia ou seja bloqueado
  // por proxy; quando o SSE esta ativo, a UI ja atualizou bem antes).
  useEffect(() => {
    if (!conversaAtivaId) return undefined;
    pollDetalheRef.current = setInterval(() => carregarDetalhe(conversaAtivaId, false), 4000);
    return () => {
      if (pollDetalheRef.current) clearInterval(pollDetalheRef.current);
      pollDetalheRef.current = null;
    };
  }, [conversaAtivaId]);

  // Mantem os refs do SSE apontando para o estado/funcoes do render atual.
  // Sem array de deps: roda apos todo render.
  useEffect(() => {
    conversaAtivaIdRef.current = conversaAtivaId;
    carregarDetalheRef.current = carregarDetalhe;
    carregarListaRef.current = carregarLista;
  });

  // Aplica o ack recebido por SSE direto na mensagem (sem refetch). Monotonico:
  // nunca rebaixa o check ja exibido (enviada<entregue<lida).
  const patchStatusLocal = (conversaId, evolutionId, status) => {
    setConversaAtiva((prev) => {
      if (!prev || prev.id !== conversaId || !Array.isArray(prev.mensagens)) return prev;
      const rank = { enviada: 1, entregue: 2, lida: 3 };
      let mudou = false;
      const mensagens = prev.mensagens.map((m) => {
        if (m.evolution_id !== evolutionId) return m;
        if ((rank[status] || 0) <= (rank[m.status] || 0)) return m;
        mudou = true;
        return { ...m, status };
      });
      return mudou ? { ...prev, mensagens } : prev;
    });
  };

  // Canal SSE de tempo real. Conecta uma vez (por sessao com permissao) e
  // reage aos eventos do backend: status de ack, nova mensagem, mudanca de
  // conversa. O EventSource reconecta sozinho em caso de queda; o polling
  // acima cobre o intervalo ate a reconexao.
  useEffect(() => {
    if (!podeUsar) return undefined;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return undefined;

    const es = new EventSource(`${API}/atendimentos/stream?token=${encodeURIComponent(token)}`);

    const agendarLista = () => {
      if (listaDebounceRef.current) return;
      listaDebounceRef.current = setTimeout(() => {
        listaDebounceRef.current = null;
        carregarListaRef.current(false);
      }, 400);
    };

    es.addEventListener('message_status', (e) => {
      try {
        const d = JSON.parse(e.data);
        patchStatusLocal(d.conversaId, d.evolutionId, d.status);
      } catch (_) { /* ignora payload malformado */ }
    });

    es.addEventListener('message_new', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId === conversaAtivaIdRef.current) {
          carregarDetalheRef.current(conversaAtivaIdRef.current, false);
        }
        agendarLista();
      } catch (_) { /* ignora */ }
    });

    es.addEventListener('conversa_update', () => {
      agendarLista();
      const ativa = conversaAtivaIdRef.current;
      if (ativa) carregarDetalheRef.current(ativa, false);
    });

    // Mensagem apagada / editada — patch local.
    es.addEventListener('message_apagada', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId !== conversaAtivaIdRef.current) return;
        setConversaAtiva((prev) => (prev ? {
          ...prev,
          mensagens: (prev.mensagens || []).map((m) => (m.id === d.mensagemId
            ? { ...m, apagada: true, apagada_em: new Date().toISOString(), conteudo: null, midia_url: null }
            : m)),
        } : prev));
      } catch (_) { /* ignora */ }
    });

    es.addEventListener('message_editada', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId !== conversaAtivaIdRef.current) return;
        setConversaAtiva((prev) => (prev ? {
          ...prev,
          mensagens: (prev.mensagens || []).map((m) => (m.id === d.mensagemId
            ? { ...m, conteudo: d.conteudo, editada_em: new Date().toISOString() }
            : m)),
        } : prev));
      } catch (_) { /* ignora */ }
    });

    // Cliente digitando/gravando — mostra selo "digitando..." no header. Auto
    // limpa em 6s por seguranca (Evolution nem sempre manda o "parou").
    es.addEventListener('presence_update', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId !== conversaAtivaIdRef.current) return;
        setPresencaCliente(d.estado === 'idle' ? null : d.estado);
        if (presencaTimerRef.current) clearTimeout(presencaTimerRef.current);
        if (d.estado !== 'idle') {
          presencaTimerRef.current = setTimeout(() => setPresencaCliente(null), 6000);
        }
      } catch (_) { /* ignora */ }
    });

    // Reacao entrou/mudou/foi removida — patch local pra nao refetch inteiro.
    es.addEventListener('message_reacao', (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.conversaId !== conversaAtivaIdRef.current) return;
        setConversaAtiva((prev) => (prev ? {
          ...prev,
          mensagens: (prev.mensagens || []).map((m) => (m.id === d.mensagemId ? { ...m, reacao: d.reacao || null } : m)),
        } : prev));
      } catch (_) { /* ignora */ }
    });

    es.onerror = () => { /* reconexao automatica do EventSource */ };

    return () => {
      es.close();
      if (listaDebounceRef.current) {
        clearTimeout(listaDebounceRef.current);
        listaDebounceRef.current = null;
      }
    };
  }, [podeUsar]);

  // Posiciona a thread na mensagem mais proxima do instante `marco` (created_at
  // do chamado) e destaca por alguns segundos. Se nao houver marco, rola pro topo.
  const posicionarNoMarco = (marco) => {
    const msgs = conversaAtiva?.mensagens || [];
    const el = mensagensRef.current;
    if (!el) return;
    if (!marco || !msgs.length) { el.scrollTop = 0; return; }
    const alvoTs = new Date(marco).getTime();
    if (Number.isNaN(alvoTs)) { el.scrollTop = 0; return; }
    let melhor = null;
    let melhorDelta = Infinity;
    for (const m of msgs) {
      const ts = new Date(m.criada_em).getTime();
      if (Number.isNaN(ts)) continue;
      const delta = Math.abs(ts - alvoTs);
      if (delta < melhorDelta) { melhorDelta = delta; melhor = m; }
    }
    if (!melhor) { el.scrollTop = 0; return; }
    const node = document.getElementById(`msg-${melhor.id}`);
    if (!node) { el.scrollTop = 0; return; }
    node.scrollIntoView({ block: 'center' });
    // Destaque temporario (faixa na linha) pro atendente achar o ponto de origem.
    node.style.transition = 'background-color 0.4s ease';
    node.style.backgroundColor = 'rgba(42,138,156,0.18)';
    node.style.borderRadius = '10px';
    setTimeout(() => {
      node.style.backgroundColor = 'transparent';
    }, 3000);
  };

  // Auto-scroll: normalmente vai pra ultima mensagem. Quando viemos do deep-link
  // "abrir conversa de origem", posiciona no instante em que o chamado nasceu
  // (so na primeira carga das mensagens).
  useEffect(() => {
    const el = mensagensRef.current;
    if (!el) return;
    if (rolarParaInicioRef.current && conversaAtiva?.mensagens?.length) {
      rolarParaInicioRef.current = false;
      const marco = marcoOrigemRef.current;
      marcoOrigemRef.current = null;
      posicionarNoMarco(marco);
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [conversaAtiva?.mensagens?.length, conversaAtivaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const escolherArquivo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const MAX = 25 * 1024 * 1024;
    if (f.size > MAX) {
      Swal.fire({ icon: 'warning', title: 'Arquivo muito grande', text: 'Limite de 25MB.' });
      e.target.value = '';
      return;
    }
    setArquivoSelecionado(f);
    e.target.value = '';
  };

  const removerArquivo = () => setArquivoSelecionado(null);

  function fmtTempoGravacao(s) {
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  async function iniciarGravacao() {
    if (!navigator.mediaDevices?.getUserMedia) {
      Swal.fire('Indisponivel', 'Seu navegador nao suporta gravacao de audio.', 'error');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      Swal.fire('Sem permissao', 'Permita o acesso ao microfone no navegador e tente novamente.', 'warning');
      return;
    }
    const mr = new MediaRecorder(stream);
    chunksRef.current = [];
    canceladoRef.current = false;
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timerGravacaoRef.current);
      setGravando(false);
      setTempoGravacao(0);
      if (canceladoRef.current) return;
      const idConversa = conversaParaEnvioRef.current;
      if (!idConversa || !chunksRef.current.length) return;
      const mime = mr.mimeType || 'audio/webm';
      const ext = mime.includes('ogg') ? 'ogg' : 'webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: mime });
      setEnviando(true);
      try {
        const fd = new FormData();
        fd.append('arquivo', file);
        await axios.post(`${API}/atendimentos/${idConversa}/mensagens`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await carregarDetalhe(idConversa, false);
        await carregarLista(false);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Falha ao enviar audio', text: err.response?.data?.message || err.message });
        await carregarDetalhe(idConversa, false);
      } finally {
        setEnviando(false);
      }
    };
    mr.start();
    mediaRecorderRef.current = mr;
    setGravando(true);
    setTempoGravacao(0);
    timerGravacaoRef.current = setInterval(() => setTempoGravacao((t) => t + 1), 1000);
  }

  function pararEEnviarGravacao() {
    conversaParaEnvioRef.current = conversaAtivaId;
    canceladoRef.current = false;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    clearInterval(timerGravacaoRef.current);
  }

  function cancelarGravacao() {
    canceladoRef.current = true;
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    clearInterval(timerGravacaoRef.current);
    setGravando(false);
    setTempoGravacao(0);
  }

  // Ctrl+V no textarea: se o clipboard tem uma imagem (print de tela, recorte,
  // arquivo copiado), trata como se o atendente tivesse anexado pelo paperclip.
  const aoColar = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (!file) continue;
        const MAX = 25 * 1024 * 1024;
        if (file.size > MAX) {
          Swal.fire({ icon: 'warning', title: 'Arquivo muito grande', text: 'Limite de 25MB.' });
          e.preventDefault();
          return;
        }
        // Print de tela vem geralmente sem nome ("image.png"). Renomeia com timestamp
        // para o atendente identificar no historico.
        const nome = file.name && file.name !== 'image.png'
          ? file.name
          : `print-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
        const arquivo = new File([file], nome, { type: file.type });
        setArquivoSelecionado(arquivo);
        e.preventDefault();
        return;
      }
    }
  };

  const enviar = async (e) => {
    e?.preventDefault?.();
    const t = texto.trim();
    if ((!t && !arquivoSelecionado) || !conversaAtivaId || enviando) return;
    setEnviando(true);
    try {
      if (arquivoSelecionado) {
        const fd = new FormData();
        fd.append('arquivo', arquivoSelecionado);
        if (t) fd.append('texto', t);
        if (chamadoEmFoco) fd.append('chamado_id', String(chamadoEmFoco));
        if (replyTo) fd.append('quoted_mensagem_id', String(replyTo.id));
        await axios.post(`${API}/atendimentos/${conversaAtivaId}/mensagens`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const body = { texto: t };
        if (chamadoEmFoco) body.chamado_id = chamadoEmFoco;
        if (replyTo) body.quoted_mensagem_id = replyTo.id;
        await axios.post(`${API}/atendimentos/${conversaAtivaId}/mensagens`, body);
      }
      setTexto('');
      setArquivoSelecionado(null);
      setReplyTo(null);
      await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Falha ao enviar',
        text: err.response?.data?.message || err.message,
      });
      await carregarDetalhe(conversaAtivaId, false);
    } finally {
      setEnviando(false);
    }
  };

  const abrirEncerrar = async () => {
    if (!conversaAtivaId) return;
    try {
      const [resCats, resModos] = await Promise.all([
        axios.get(`${API}/atendimento/categorias`),
        axios.get(`${API}/atendimento/motivos`, { params: { ativos: 'true' } }),
      ]);
      setCategoriasEncerrar((resCats.data?.data || []).filter((c) => c.ativo));
      setModosEncerrar(resModos.data?.data || []);
    } catch (e) {
      setCategoriasEncerrar([]);
      setModosEncerrar([]);
    }
    setEncerrarAberto(true);
  };

  const confirmarEncerrar = async (payload) => {
    if (!conversaAtivaId) return;
    try {
      const res = await axios.post(`${API}/atendimentos/${conversaAtivaId}/encerrar`, payload);
      const { chamado_criado: chamadoCriado, chamado_resolvido: chamadoResolvido } = res.data?.data || {};
      setEncerrarAberto(false);
      // Se a conversa estava encerrada (modo "Resolver chamados"), nao limpa o painel
      if (conversaAtiva?.status !== 'encerrada') {
        setConversaAtivaId(null);
      } else {
        await carregarChamadosVinculados(conversaAtivaId);
      }
      await carregarLista(false);
      if (chamadoCriado?.id) {
        Swal.fire({
          icon: 'success',
          title: 'Encerrado',
          text: `Chamado #${chamadoCriado.id} registrado com sucesso.`,
          timer: 1800,
          showConfirmButton: false,
        });
      } else if (chamadoResolvido?.id) {
        Swal.fire({
          icon: 'success',
          title: 'Encerrado',
          text: `Chamado #${chamadoResolvido.id} marcado como resolvido.`,
          timer: 1800,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
      throw err;
    }
  };

  const assumir = async () => {
    if (!conversaAtivaId) return;
    try {
      await axios.patch(`${API}/atendimentos/${conversaAtivaId}/status`, {
        status: 'em_atendimento',
        assumir: true,
      });
      await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
    }
  };

  const transferir = async () => {
    if (!conversaAtivaId || !conversaAtiva) return;
    const opcoes = {};
    canais
      .filter((c) => c.id !== conversaAtiva.fila_canal_id)
      .forEach((c) => { opcoes[c.id] = c.nome; });
    if (Object.keys(opcoes).length === 0) {
      Swal.fire('Sem canais', 'Não há outro canal para transferir.', 'info');
      return;
    }
    const { value: destino } = await Swal.fire({
      title: 'Transferir atendimento',
      text: 'O contato vai para a fila do canal escolhido. As respostas continuam saindo pelo mesmo número.',
      input: 'select',
      inputOptions: opcoes,
      inputPlaceholder: 'Selecione o canal de destino',
      showCancelButton: true,
      confirmButtonText: 'Transferir',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v ? 'Selecione um canal' : undefined),
    });
    if (!destino) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/transferir`, { fila_canal_id: Number(destino) });
      // Saiu da minha fila: fecha o detalhe e atualiza a lista.
      setConversaAtivaId(null);
      setConversaAtiva(null);
      await carregarLista(false);
      Swal.fire({ icon: 'success', title: 'Transferido', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || err.message });
    }
  };

  const ninguemAssumiu = conversaAtiva?.status !== 'encerrada' && !conversaAtiva?.atendente;
  const outroAssumiu = conversaAtiva?.status !== 'encerrada'
    && !!conversaAtiva?.atendente
    && conversaAtiva?.atendente !== user?.usuario;

  // Multicanal v2: conjuntos de canais que o usuario VE e em que pode AGIR.
  // verTodos = fallback de token antigo (sem canais_extra) com ve_todos_canais.
  const meuEscopo = (() => {
    const padrao = user?.canal_id != null ? Number(user.canal_id) : null;
    const temNovo = Array.isArray(user?.canais_extra);
    const extras = temNovo ? user.canais_extra.map(Number).filter(Boolean) : [];
    const assumir = !!user?.assumir_outros;
    const verTodos = !temNovo && !!user?.ve_todos_canais;
    const dedupe = (a) => [...new Set(a.filter((v) => v != null))];
    const canaisVer = padrao != null ? dedupe([padrao, ...extras]) : dedupe(extras);
    const canaisAtuar = padrao != null ? dedupe([padrao, ...(assumir ? extras : [])]) : [];
    return { verTodos, canaisVer, canaisAtuar };
  })();
  const filaAtiva = conversaAtiva ? Number(conversaAtiva.fila_canal_id ?? conversaAtiva.canal_id) : null;

  // Pode AGIR (assumir/responder/encerrar): fila no conjunto de atuacao. Tolerante
  // a escopo desconhecido (token/cache antigo) — backend decide com 403.
  const podeAgir = !conversaAtiva
    || meuEscopo.canaisAtuar.length === 0
    || meuEscopo.canaisAtuar.includes(filaAtiva);

  // Transferir = roteamento: basta ENXERGAR a conversa (espelha o backend).
  const podeTransferir = !!conversaAtiva
    && (meuEscopo.verTodos || meuEscopo.canaisVer.length === 0 || meuEscopo.canaisVer.includes(filaAtiva));

  // Ctrl+F com uma conversa aberta abre a barra de busca da conversa (em vez
  // do "find" nativo do browser, que so casaria com o que ja renderizou e
  // nao rolaria pra fora do viewport).
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && conversaAtivaIdRef.current) {
        e.preventDefault();
        setBuscaAberta(true);
        setTimeout(() => buscaInputRef.current?.focus(), 30);
      } else if (e.key === 'Escape' && buscaAberta) {
        setBuscaAberta(false);
        setBuscaTermo('');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [buscaAberta]);

  // Fecha a busca ao trocar de conversa (o resultado deixa de fazer sentido).
  useEffect(() => {
    setBuscaAberta(false);
    setBuscaTermo('');
    setBuscaHitAtivo(0);
    setPresencaCliente(null);
  }, [conversaAtivaId]);

  // Ids das mensagens que casam com o termo, na ordem em que aparecem.
  // Casa em conteudo, midia_nome (nome do documento) e no push_name do quoted.
  const buscaHits = useMemo(() => {
    const termo = buscaTermo.trim().toLowerCase();
    if (!termo || !conversaAtiva?.mensagens?.length) return [];
    return conversaAtiva.mensagens
      .filter((m) => (m.conteudo || '').toLowerCase().includes(termo))
      .map((m) => m.id);
  }, [buscaTermo, conversaAtiva?.mensagens]);

  // Mantem o hit ativo dentro do range e rola pra ele.
  useEffect(() => {
    if (buscaHits.length === 0) return;
    const idx = Math.min(buscaHitAtivo, buscaHits.length - 1);
    if (idx !== buscaHitAtivo) setBuscaHitAtivo(idx);
    const alvo = document.getElementById(`msg-${buscaHits[idx]}`);
    if (alvo) alvo.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [buscaHits, buscaHitAtivo]);

  function proximoHit(delta) {
    if (!buscaHits.length) return;
    setBuscaHitAtivo((cur) => (cur + delta + buscaHits.length) % buscaHits.length);
  }

  // Aplica a resposta rapida no textarea. Substitui a query `/token` corrente
  // (do cursor pra tras) pelo texto expandido; ou, quando vem do picker ⚡,
  // insere no cursor. Depois foca o textarea de novo.
  const aplicarRespostaRapida = (resposta, { substituirToken = false } = {}) => {
    const el = textareaRef.current;
    const inicio = el?.selectionStart ?? texto.length;
    const antes = texto.slice(0, inicio);
    const depois = texto.slice(inicio);
    let novoAntes = antes;
    if (substituirToken) {
      const match = antes.match(/(?:^|\s)(\/[\w.\-]*)$/);
      if (match) {
        novoAntes = antes.slice(0, antes.length - match[1].length);
      }
    }
    const insercao = resposta.texto + (depois.startsWith(' ') || depois === '' ? '' : ' ');
    const novoTexto = novoAntes + insercao + depois;
    setTexto(novoTexto);
    setRespostaSugestoes(null);
    setRespostaPickerAberto(false);
    setTimeout(() => {
      const pos = novoAntes.length + insercao.length;
      el?.focus();
      el?.setSelectionRange(pos, pos);
    }, 20);
  };

  // Sinaliza "digitando" pro cliente. A Evolution auto-expira em ~3s, entao
  // reenviamos a cada 2s enquanto o atendente esta digitando. Debounce de 400ms
  // no lado local pra nao mandar 1 request por tecla — e cache de 2s garante
  // que so sai UM composing quando dispara.
  const sinalizarDigitando = () => {
    if (!conversaAtivaId) return;
    const agora = Date.now();
    if (agora - atendenteTypingUltimoEnvioRef.current < 2000) return;
    atendenteTypingUltimoEnvioRef.current = agora;
    axios.post(`${API}/atendimentos/${conversaAtivaId}/presenca`, { presence: 'composing' })
      .catch(() => { /* silencia — presence e best-effort */ });
  };

  // Reage a cada tecla no textarea: se o "token" logo antes do cursor comeca
  // com "/", mostra sugestoes; senao esconde.
  const atualizarSugestoesRapidas = (valor, cursorPos) => {
    const antes = valor.slice(0, cursorPos);
    const match = antes.match(/(?:^|\s)(\/[\w.\-]*)$/);
    if (!match) { setRespostaSugestoes(null); return; }
    const termo = match[1].slice(1).toLowerCase(); // remove "/"
    const candidatas = (respostasRapidas || [])
      .filter((r) => r.ativo !== false)
      .filter((r) => !termo || r.atalho.startsWith(termo))
      .slice(0, 6);
    setRespostaSugestoes(candidatas.length ? candidatas : null);
  };

  const abrirEnviarLoc = () => {
    setLocForm({ latitude: '', longitude: '', name: '', address: '' });
    setLocModalAberto(true);
  };

  const pegarLocalizacaoAtual = () => {
    if (!navigator.geolocation) {
      Swal.fire({ icon: 'error', title: 'GPS indisponivel', text: 'Seu navegador nao expoe geolocalizacao.' });
      return;
    }
    setLocBuscandoGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocForm((f) => ({
          ...f,
          latitude: String(pos.coords.latitude.toFixed(6)),
          longitude: String(pos.coords.longitude.toFixed(6)),
        }));
        setLocBuscandoGps(false);
      },
      (err) => {
        setLocBuscandoGps(false);
        Swal.fire({ icon: 'error', title: 'Falha no GPS', text: err.message });
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const enviarLocalizacao = async () => {
    if (!conversaAtivaId) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/localizacao`, {
        latitude: Number(locForm.latitude),
        longitude: Number(locForm.longitude),
        name: locForm.name || undefined,
        address: locForm.address || undefined,
      });
      setLocModalAberto(false);
      await carregarDetalhe(conversaAtivaId, false);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Nao deu pra enviar', text: e.response?.data?.message || e.message });
    }
  };

  const abrirEnviarContato = () => {
    setContatoBuscaAg('');
    setContatoAgResultados([]);
    setContatoManual({ fullName: '', phoneNumber: '' });
    setContatoModalAberto(true);
  };

  // Busca contatos da agenda (mesma /contatos usada em Nova conversa).
  useEffect(() => {
    if (!contatoModalAberto || !contatoBuscaAg.trim()) { setContatoAgResultados([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/contatos`, { params: { busca: contatoBuscaAg.trim() } });
        setContatoAgResultados((res.data?.data || []).slice(0, 20));
      } catch (_) { setContatoAgResultados([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [contatoBuscaAg, contatoModalAberto]);

  const enviarContato = async (payload) => {
    if (!conversaAtivaId || !payload) return;
    try {
      await axios.post(`${API}/atendimentos/${conversaAtivaId}/contato`, payload);
      setContatoModalAberto(false);
      await carregarDetalhe(conversaAtivaId, false);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Nao deu pra enviar', text: e.response?.data?.message || e.message });
    }
  };

  const apagarMensagem = async (msg) => {
    if (!conversaAtivaId || !msg?.id) return;
    const c = await Swal.fire({
      icon: 'warning', title: 'Apagar pra todos?',
      text: 'O cliente vera "Esta mensagem foi apagada". Nao da pra desfazer.',
      showCancelButton: true, confirmButtonText: 'Apagar',
      cancelButtonText: 'Cancelar', confirmButtonColor: '#b43737',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}`);
      // SSE avisa; o carregarDetalhe cobre eventual gap.
      await carregarDetalhe(conversaAtivaId, false);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Nao deu pra apagar', text: e.response?.data?.message || e.message });
    }
  };

  const editarMensagemPrompt = async (msg) => {
    if (!conversaAtivaId || !msg?.id) return;
    const r = await Swal.fire({
      title: 'Editar mensagem',
      input: 'textarea',
      inputValue: msg.conteudo || '',
      inputAttributes: { maxlength: 4096, autocapitalize: 'off' },
      showCancelButton: true, confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!v || !v.trim() ? 'Texto obrigatorio' : undefined),
    });
    if (!r.isConfirmed) return;
    const novo = r.value.trim();
    if (novo === (msg.conteudo || '')) return;
    try {
      await axios.put(`${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}`, { texto: novo });
      await carregarDetalhe(conversaAtivaId, false);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Nao deu pra editar', text: e.response?.data?.message || e.message });
    }
  };

  const reagirMensagem = async (msg, emoji) => {
    if (!conversaAtivaId || !msg?.id) return;
    // Otimista: aplica na lista local; SSE (message_reacao) confirma depois.
    setConversaAtiva((prev) => (prev ? {
      ...prev,
      mensagens: (prev.mensagens || []).map((x) => (x.id === msg.id ? { ...x, reacao: emoji || null } : x)),
    } : prev));
    try {
      await axios.post(
        `${API}/atendimentos/${conversaAtivaId}/mensagens/${msg.id}/reacao`,
        { reaction: emoji || '' },
      );
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Nao deu pra reagir', text: e.response?.data?.message || e.message });
      // Rollback: recarrega detalhe pra desfazer o otimista.
      await carregarDetalhe(conversaAtivaId, false);
    }
  };

  const confirmarEncaminhar = async (conversaDestino) => {
    if (!encaminharMsg || !conversaDestino || encaminhando) return;
    setEncaminhando(true);
    try {
      await axios.post(
        `${API}/atendimentos/${conversaAtivaId}/mensagens/${encaminharMsg.id}/encaminhar`,
        { para_conversa_id: conversaDestino.id },
      );
      setEncaminharMsg(null);
      setEncaminharBusca('');
      // Se encaminhou pra conversa aberta, o SSE ja atualiza. Senao, so avisa.
      if (Number(conversaDestino.id) === Number(conversaAtivaId)) {
        await carregarDetalhe(conversaAtivaId, false);
      }
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Nao deu pra encaminhar',
        text: e.response?.data?.message || e.message,
      });
    } finally {
      setEncaminhando(false);
    }
  };

  const abrirContatoSidebar = async () => {
    if (!conversaAtiva?.contato_id) return;
    setContatoSidebarForm({
      push_name: conversaAtiva.push_name || '',
      filial: conversaAtiva.contato_filial || '',
      setor: conversaAtiva.contato_setor || '',
    });
    setContatoSidebarErro('');
    setContatoSidebarAberta(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/atendimento/filiais`),
        axios.get(`${API}/atendimento/setores`, { params: { ativos: 'true' } }),
      ]);
      setContatoSidebarFiliais(r1.data?.data || []);
      setContatoSidebarSetores(r2.data?.data || []);
    } catch { /* silencioso */ }
  };

  const salvarContatoSidebar = async () => {
    if (!conversaAtiva?.contato_id) return;
    if (!contatoSidebarForm.push_name.trim()) {
      setContatoSidebarErro('Nome é obrigatório.');
      return;
    }
    setContatoSidebarSalvando(true);
    setContatoSidebarErro('');
    try {
      await axios.put(`${API}/contatos/${conversaAtiva.contato_id}`, {
        push_name: contatoSidebarForm.push_name.trim(),
        filial: contatoSidebarForm.filial || null,
        setor: contatoSidebarForm.setor || null,
      });
      setContatoSidebarAberta(false);
      await carregarDetalhe(conversaAtivaId, false);
      await carregarLista(false);
    } catch (err) {
      setContatoSidebarErro(err.response?.data?.message || 'Erro ao salvar contato.');
    } finally {
      setContatoSidebarSalvando(false);
    }
  };

  // Atendentes unicos presentes nas conversas carregadas (todos os status).
  // So aparece o filtro quando ha 2+ atendentes distintos.
  const atendentesDisponiveis = useMemo(() => {
    const set = new Set();
    for (const c of conversas) {
      if (c.atendente) set.add(c.atendente);
    }
    return Array.from(set).sort();
  }, [conversas]);

  // Reset do filtro de atendente quando os atendentes saem da lista carregada.
  useEffect(() => {
    if (atendentesFiltro.some((a) => !atendentesDisponiveis.includes(a))) {
      setAtendentesFiltro([]);
    }
  }, [atendentesDisponiveis, atendentesFiltro]);

  const conversasOrdenadas = useMemo(
    () => [...conversas]
      .filter((c) => statusFiltro || c.status !== 'encerrada')
      .filter((c) => atendentesFiltro.length === 0 || atendentesFiltro.includes(c.atendente))
      .sort((a, b) => {
        const ax = a.ultima_mensagem_em ? new Date(a.ultima_mensagem_em).getTime() : 0;
        const bx = b.ultima_mensagem_em ? new Date(b.ultima_mensagem_em).getTime() : 0;
        return bx - ax;
      }),
    [conversas, statusFiltro, atendentesFiltro],
  );

  if (!podeUsar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Voce nao tem permissao para acessar atendimentos.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell" style={{ height: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column' }}>
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ marginBottom: 0 }}>Atendimentos</h1>
            <InfoFluxoAtendimento />
          </div>
          <p>Conversas WhatsApp em andamento. Selecione uma para responder.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href="/ti/contatos"
            className="btn btn-sm btn-outline-secondary"
            title="Gerenciar contatos cadastrados"
          >
            <i className="bi bi-person-vcard me-1" /> Contatos
          </a>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setNovaConversaAberta(true)}
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-circle me-1" /> Nova conversa
          </button>
        </div>
      </section>

      <NovaConversaModal
        aberto={novaConversaAberta}
        onClose={() => setNovaConversaAberta(false)}
        onCriada={(conversa) => {
          setNovaConversaAberta(false);
          if (conversa?.id) {
            setConversaAtivaId(conversa.id);
            carregarLista(false);
          }
        }}
      />

      <EncerrarModal
        aberto={encerrarAberto}
        conversaAtiva={conversaAtiva}
        categorias={categoriasEncerrar}
        modos={modosEncerrar}
        chamadoEmFoco={chamadoEmFoco}
        chamadosVinculados={chamadosVinculados}
        onClose={() => setEncerrarAberto(false)}
        onConfirmar={confirmarEncerrar}
      />

      <LightboxImagem url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

      {encaminharMsg && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setEncaminharMsg(null); setEncaminharBusca(''); } }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            className="content-card"
            style={{
              width: '100%', maxWidth: 480, maxHeight: '80vh',
              display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--portal-border)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <i className="bi bi-arrow-90deg-right" />
              <strong style={{ flex: 1 }}>Encaminhar mensagem</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setEncaminharMsg(null); setEncaminharBusca(''); }}
              ><i className="bi bi-x-lg" /></button>
            </div>
            <div style={{ padding: 12, borderBottom: '1px solid var(--portal-border)' }}>
              <input
                type="search"
                autoFocus
                placeholder="Filtrar por nome ou numero..."
                value={encaminharBusca}
                onChange={(e) => setEncaminharBusca(e.target.value)}
                style={{
                  width: '100%', border: '1px solid var(--portal-border)',
                  borderRadius: 6, padding: '6px 10px', fontSize: '0.85rem',
                  background: 'var(--portal-surface)', color: 'var(--portal-text)',
                }}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {(() => {
                const termo = encaminharBusca.trim().toLowerCase();
                const lista = (conversas || []).filter((c) => {
                  if (Number(c.id) === Number(conversaAtivaId)) return false; // nao pra ela mesma
                  if (!termo) return true;
                  const nome = (c.push_name || '').toLowerCase();
                  const num = String(c.numero || '');
                  return nome.includes(termo) || num.includes(termo);
                });
                if (lista.length === 0) {
                  return (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                      Nenhuma conversa {termo ? 'casa com a busca' : 'disponivel'}.
                    </div>
                  );
                }
                return lista.slice(0, 50).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={encaminhando}
                    onClick={() => confirmarEncaminhar(c)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 14px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      borderBottom: '1px solid var(--portal-border)',
                      color: 'var(--portal-text)', textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 600, fontSize: '0.8rem',
                    }}>
                      {iniciaisDe(c.push_name, c.numero)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.push_name || formatNumero(c.numero)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                        {formatNumero(c.numero)} · {c.canal_nome}
                      </div>
                    </div>
                    {encaminhando && <div className="screen-loader__spinner" style={{ width: 16, height: 16 }} />}
                  </button>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {locModalAberto && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setLocModalAberto(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div className="content-card" style={{ width: '100%', maxWidth: 460, padding: 20 }}>
            <h5 style={{ marginBottom: 12 }}>
              <i className="bi bi-geo-alt me-2" /> Enviar localizacao
            </h5>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" className="btn btn-sm btn-outline-info"
                onClick={pegarLocalizacaoAtual} disabled={locBuscandoGps}>
                <i className="bi bi-crosshair me-1" />
                {locBuscandoGps ? 'Buscando GPS...' : 'Usar minha localizacao'}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <input type="number" step="any" placeholder="Latitude"
                value={locForm.latitude} onChange={(e) => setLocForm((f) => ({ ...f, latitude: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
              <input type="number" step="any" placeholder="Longitude"
                value={locForm.longitude} onChange={(e) => setLocForm((f) => ({ ...f, longitude: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
            </div>
            <input type="text" placeholder="Nome (opcional)" maxLength={100}
              value={locForm.name} onChange={(e) => setLocForm((f) => ({ ...f, name: e.target.value }))}
              style={{ width: '100%', marginBottom: 8, padding: '6px 10px', borderRadius: 6,
                border: '1px solid var(--portal-border)',
                background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
            <input type="text" placeholder="Endereco (opcional)" maxLength={200}
              value={locForm.address} onChange={(e) => setLocForm((f) => ({ ...f, address: e.target.value }))}
              style={{ width: '100%', marginBottom: 12, padding: '6px 10px', borderRadius: 6,
                border: '1px solid var(--portal-border)',
                background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setLocModalAberto(false)}>
                Cancelar
              </button>
              <button type="button" className="btn" onClick={enviarLocalizacao}
                disabled={!locForm.latitude || !locForm.longitude}
                style={{ background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                  color: '#fff', fontWeight: 600, border: 'none', padding: '6px 18px' }}>
                <i className="bi bi-send-fill me-1" /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {contatoModalAberto && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setContatoModalAberto(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div className="content-card" style={{ width: '100%', maxWidth: 480, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--portal-border)' }}>
              <h5 style={{ marginBottom: 0 }}>
                <i className="bi bi-person-lines-fill me-2" /> Enviar contato
              </h5>
            </div>
            <div style={{ padding: 12, borderBottom: '1px solid var(--portal-border)' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Da agenda</label>
              <input type="search" autoFocus placeholder="Buscar por nome ou numero..."
                value={contatoBuscaAg} onChange={(e) => setContatoBuscaAg(e.target.value)}
                style={{ width: '100%', marginTop: 4, padding: '6px 10px', borderRadius: 6,
                  border: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 60 }}>
              {contatoAgResultados.length === 0 ? (
                <div style={{ padding: 12, color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                  {contatoBuscaAg.trim() ? 'Nenhum contato encontrado.' : 'Digite pra buscar na agenda.'}
                </div>
              ) : (
                contatoAgResultados.map((c) => (
                  <button key={c.id} type="button"
                    onClick={() => enviarContato({ fullName: c.push_name || c.numero, phoneNumber: c.numero })}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                      borderBottom: '1px solid var(--portal-border)', textAlign: 'left',
                      color: 'var(--portal-text)' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 600, fontSize: '0.8rem', flexShrink: 0 }}>
                      {iniciaisDe(c.push_name, c.numero)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                        {c.push_name || formatNumero(c.numero)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                        {formatNumero(c.numero)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
            <div style={{ padding: 12, borderTop: '1px solid var(--portal-border)' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Ou digite manualmente</label>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <input type="text" placeholder="Nome" maxLength={100}
                  value={contatoManual.fullName}
                  onChange={(e) => setContatoManual((c) => ({ ...c, fullName: e.target.value }))}
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6,
                    border: '1px solid var(--portal-border)',
                    background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
                <input type="tel" placeholder="Telefone"
                  value={contatoManual.phoneNumber}
                  onChange={(e) => setContatoManual((c) => ({ ...c, phoneNumber: e.target.value }))}
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6,
                    border: '1px solid var(--portal-border)',
                    background: 'var(--portal-surface)', color: 'var(--portal-text)' }} />
                <button type="button" className="btn btn-sm"
                  onClick={() => enviarContato(contatoManual)}
                  disabled={!contatoManual.fullName.trim() || !contatoManual.phoneNumber.trim()}
                  style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}>
                  <i className="bi bi-send-fill" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      <div
        style={{
          flex: 1,
          display: 'grid',
          // Mobile: uma coluna so, lista OU conversa (estilo WhatsApp).
          // Desktop: lista + conversa (+ ficha do contato quando aberta).
          gridTemplateColumns: isMobile
            ? '1fr'
            : `minmax(280px, 360px) 1fr${contatoSidebarAberta ? ' 300px' : ''}`,
          gap: isMobile ? 0 : 12,
          minHeight: 0,
        }}
      >
        {/* Lista — no mobile some quando uma conversa esta aberta. */}
        {(!isMobile || !conversaAtivaId) && (
        <aside
          className="content-card"
          style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--portal-border)' }}>
            <input
              type="search"
              placeholder="Buscar por nome, numero ou texto..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', borderRadius: 6,
                border: '1px solid var(--portal-border)',
                background: 'var(--portal-surface)', color: 'var(--portal-text)',
                fontSize: '0.85rem',
              }}
            />

            {/* Filtro de status */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {['', 'aberta', 'em_atendimento', 'encerrada'].map((opt) => (
                <button
                  key={opt || 'todas'}
                  type="button"
                  onClick={() => setStatusFiltro(opt)}
                  style={{
                    fontSize: '0.7rem',
                    padding: '3px 10px',
                    borderRadius: 12,
                    border: '1px solid var(--portal-border)',
                    background: statusFiltro === opt ? '#2A8A9C' : 'transparent',
                    color: statusFiltro === opt ? '#fff' : 'var(--portal-text)',
                    cursor: 'pointer',
                  }}
                >
                  {opt === '' ? 'Todas' : STATUS_LABEL[opt]}
                </button>
              ))}
            </div>

            {/* Filtro de Canal: aparece para quem ve mais de um canal, listando so
                os visiveis. Filtro de Atendente: so o papel de gestao. */}
            {(() => {
              const canaisVisiveis = meuEscopo.verTodos
                ? canais
                : canais.filter((c) => meuEscopo.canaisVer.includes(c.id));
              const mostraCanal = canaisVisiveis.length > 1;
              const mostraAtendente = temPermissao('ti:atendimentos:todos');
              if (!mostraCanal && !mostraAtendente) return null;
              return (
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {mostraCanal && (
                    <MultiSelectDropdown
                      label="Canal"
                      options={canaisVisiveis.map((c) => ({ value: c.id, label: c.nome }))}
                      selected={canaisFiltro}
                      onChange={setCanaisFiltro}
                    />
                  )}
                  {mostraAtendente && (
                    <MultiSelectDropdown
                      label="Atendente"
                      options={atendentesDisponiveis.map((a) => ({ value: a, label: a }))}
                      selected={atendentesFiltro}
                      onChange={setAtendentesFiltro}
                    />
                  )}
                </div>
              );
            })()}
          </div>

          {!meuEscopo.verTodos && meuEscopo.canaisVer.length === 0 && (
            <div style={{ margin: '0 0 8px', padding: 10, borderRadius: 8, background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.4)', fontSize: '0.82rem' }}>
              <i className="bi bi-exclamation-triangle me-2" style={{ color: '#f0ad4e' }} />
              Você não está vinculado a um canal. Fale com o administrador para atender.
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loadingLista ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <div className="screen-loader__spinner" />
              </div>
            ) : conversasOrdenadas.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                Nenhuma conversa encontrada.
              </div>
            ) : (
              conversasOrdenadas.map((c) => (
                <ConversaItem
                  key={c.id}
                  conversa={c}
                  ativa={c.id === conversaAtivaId}
                  onClick={() => setConversaAtivaId(c.id)}
                  agora={agora}
                  horarios={horarios}
                  horarioAtivo={horarioAtivo}
                  slaTtfrMap={slaTtfrMap}
                  slaTtrMap={slaTtrMap}
                />
              ))
            )}
          </div>
        </aside>
        )}

        {/* Chat — no mobile ocupa a tela inteira quando a conversa esta aberta. */}
        {(!isMobile || conversaAtivaId) && (
        <section
          className="content-card"
          style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', minHeight: 0 }}
        >
          {!conversaAtivaId ? (
            <div className="empty-state" style={{ margin: 'auto' }}>
              <strong>Selecione uma conversa</strong>
              <span>Escolha uma conversa na lista para visualizar e responder.</span>
            </div>
          ) : loadingDetalhe && !conversaAtiva ? (
            <div style={{ margin: 'auto', textAlign: 'center' }}>
              <div className="screen-loader__spinner" />
              <p>Carregando conversa...</p>
            </div>
          ) : conversaAtiva ? (
            <>
              <header
                style={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12,
                  // No mobile imersivo, o topo vai sob a status bar do iOS — o
                  // safe-area empurra o header pra baixo dela.
                  padding: isMobile
                    ? 'calc(env(safe-area-inset-top, 0px) + 8px) 10px 8px'
                    : '12px 16px',
                  borderBottom: '1px solid var(--portal-border)',
                  flexWrap: 'wrap',
                }}
              >
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => setConversaAtivaId(null)}
                    title="Voltar para a lista"
                    aria-label="Voltar"
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--portal-text)',
                      fontSize: '1.5rem', lineHeight: 1, padding: '2px 4px', cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <i className="bi bi-arrow-left" />
                  </button>
                )}
                <div
                  role="button"
                  onClick={abrirContatoSidebar}
                  title="Editar contato"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer', borderRadius: 8, padding: '2px 6px',
                    transition: 'background 0.15s',
                    // `flex: 1 1 auto` + `minWidth: 0` deixam o bloco encolher
                    // ate o ellipsis em vez de empurrar os botoes pra fora
                    // (bug do fio: telefone quebrava letra a letra).
                    flex: '1 1 auto', minWidth: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 600, flexShrink: 0,
                    }}
                  >
                    {iniciaisDe(conversaAtiva.push_name, conversaAtiva.numero)}
                  </div>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <strong style={{
                      fontSize: '1rem', display: 'block',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {conversaAtiva.push_name || formatNumero(conversaAtiva.numero) || conversaAtiva.jid}
                    </strong>
                    <div style={{
                      fontSize: '0.75rem', color: 'var(--portal-muted)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {presencaCliente === 'digitando' ? (
                        <span style={{ color: '#4CB553', fontStyle: 'italic' }}>
                          <i className="bi bi-three-dots me-1" />digitando…
                        </span>
                      ) : presencaCliente === 'gravando' ? (
                        <span style={{ color: '#4CB553', fontStyle: 'italic' }}>
                          <i className="bi bi-mic-fill me-1" />gravando audio…
                        </span>
                      ) : (
                        <>
                          {formatNumero(conversaAtiva.numero)} - canal {conversaAtiva.canal_nome}
                          {conversaAtiva.atendente && ` - atendente: ${conversaAtiva.atendente}`}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <span style={{
                  fontSize: '0.7rem', fontWeight: 600,
                  background: `${STATUS_COLOR[conversaAtiva.status] || '#5E7A82'}22`,
                  color: STATUS_COLOR[conversaAtiva.status] || '#5E7A82',
                  padding: '3px 9px', borderRadius: 10,
                  border: `1px solid ${STATUS_COLOR[conversaAtiva.status] || '#5E7A82'}55`,
                }}>
                  {STATUS_LABEL[conversaAtiva.status] || conversaAtiva.status}
                </span>
                <CronometroSLA
                  chamadoCreatedAt={conversaAtiva.chamado_created_at}
                  primeiraRespostaEm={conversaAtiva.primeira_resposta_em}
                  chamadoStatus={conversaAtiva.chamado_status}
                  agora={agora}
                  horarios={horarios}
                  horarioAtivo={horarioAtivo}
                  status={conversaAtiva.status}
                  prioridade={conversaAtiva.chamado_prioridade}
                  slaTtfrMap={slaTtfrMap}
                  slaTtrMap={slaTtrMap}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setBuscaAberta((v) => !v);
                      setTimeout(() => buscaInputRef.current?.focus(), 30);
                    }}
                    title="Buscar nesta conversa (Ctrl+F)"
                  >
                    <i className="bi bi-search" />
                  </button>
                  {podeAgir && conversaAtiva.status !== 'encerrada' && conversaAtiva.atendente !== user?.usuario && (
                    <button type="button" className="btn btn-sm btn-outline-info" onClick={assumir}>
                      <i className="bi bi-person-check me-1" /> Assumir
                    </button>
                  )}
                  {podeTransferir && conversaAtiva.status !== 'encerrada' && (
                    <button type="button" className="btn btn-sm btn-outline-warning" onClick={transferir}>
                      <i className="bi bi-arrow-left-right me-1" /> Transferir
                    </button>
                  )}
                  {podeAgir && (conversaAtiva.status !== 'encerrada' || chamadosVinculados.length > 0) && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={abrirEncerrar}>
                      <i className="bi bi-check2-circle me-1" />
                      {conversaAtiva.status === 'encerrada' ? 'Resolver chamados' : 'Encerrar'}
                    </button>
                  )}
                </div>

                {/* Chips de chamados em aberto desse contato. Clicar define o
                    "chamado em foco" para o qual mensagens enviadas serao
                    vinculadas via siti_mensagem.chamado_id. */}
                {chamadosVinculados.length > 0 && (
                  <div style={{
                    flexBasis: '100%',
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                    paddingTop: 8,
                    borderTop: '1px dashed var(--portal-border)',
                  }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', alignSelf: 'center' }}>
                      Chamados em aberto:
                    </span>
                    {chamadosVinculados.map((c) => {
                      const ativo = c.id === chamadoEmFoco;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setChamadoEmFoco(ativo ? null : c.id)}
                          title={c.titulo}
                          style={{
                            background: ativo ? '#2A8A9C' : 'rgba(42,138,156,0.12)',
                            color: ativo ? '#fff' : 'var(--portal-text)',
                            border: `1px solid ${ativo ? '#2A8A9C' : 'rgba(42,138,156,0.45)'}`,
                            padding: '3px 10px',
                            borderRadius: 14,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            maxWidth: 220,
                          }}
                        >
                          <span>#{c.id}</span>
                          <span style={{
                            fontWeight: 400, opacity: 0.85,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap', maxWidth: 160,
                          }}>
                            {c.titulo}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </header>

              {buscaAberta && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px',
                  borderBottom: '1px solid var(--portal-border)',
                  background: 'rgba(42,138,156,0.06)',
                }}>
                  <i className="bi bi-search" style={{ opacity: 0.7 }} />
                  <input
                    ref={buscaInputRef}
                    type="text"
                    value={buscaTermo}
                    onChange={(e) => { setBuscaTermo(e.target.value); setBuscaHitAtivo(0); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); proximoHit(e.shiftKey ? -1 : 1); }
                      if (e.key === 'Escape') { setBuscaAberta(false); setBuscaTermo(''); }
                    }}
                    placeholder="Buscar nesta conversa..."
                    style={{
                      flex: 1, border: '1px solid var(--portal-border)',
                      borderRadius: 6, padding: '4px 10px', fontSize: '0.85rem',
                      background: 'var(--portal-surface)', color: 'var(--portal-text)',
                    }}
                  />
                  <span style={{
                    fontSize: '0.75rem', color: 'var(--portal-muted)',
                    minWidth: 60, textAlign: 'right',
                  }}>
                    {buscaTermo
                      ? (buscaHits.length === 0
                        ? 'nenhum'
                        : `${buscaHitAtivo + 1} de ${buscaHits.length}`)
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={buscaHits.length === 0}
                    onClick={() => proximoHit(-1)}
                    title="Anterior (Shift+Enter)"
                  ><i className="bi bi-chevron-up" /></button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={buscaHits.length === 0}
                    onClick={() => proximoHit(1)}
                    title="Proximo (Enter)"
                  ><i className="bi bi-chevron-down" /></button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setBuscaAberta(false); setBuscaTermo(''); }}
                    title="Fechar (Esc)"
                  ><i className="bi bi-x-lg" /></button>
                </div>
              )}

              <div
                ref={mensagensRef}
                style={{
                  flex: 1, overflowY: 'auto', padding: 16,
                  background: 'rgba(0,0,0,0.15)',
                }}
              >
                {(conversaAtiva.mensagens || []).length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                    Nenhuma mensagem ainda.
                  </div>
                ) : (
                  conversaAtiva.mensagens.map((m) => {
                    const idxHit = buscaHits.indexOf(m.id);
                    const ehHit = idxHit >= 0;
                    const ehHitAtivo = ehHit && idxHit === buscaHitAtivo;
                    return (
                      <div
                        key={m.id}
                        id={`msg-${m.id}`}
                        style={ehHit ? {
                          margin: '2px -8px',
                          padding: '2px 8px',
                          borderRadius: 6,
                          background: ehHitAtivo
                            ? 'rgba(240,173,78,0.22)'
                            : 'rgba(240,173,78,0.08)',
                          boxShadow: ehHitAtivo
                            ? '0 0 0 1px rgba(240,173,78,0.6)'
                            : 'none',
                          transition: 'background 0.15s',
                        } : undefined}
                      >
                        <MessageBubble
                          mensagem={m}
                          contatoNome={conversaAtiva.push_name}
                          contatoNumero={conversaAtiva.numero}
                          onAbrirImagem={setLightboxUrl}
                          onResponder={(msg) => {
                            setReplyTo(msg);
                            setTimeout(() => textareaRef.current?.focus(), 50);
                          }}
                          onEncaminhar={(msg) => { setEncaminharMsg(msg); setEncaminharBusca(''); }}
                          onReagir={reagirMensagem}
                          onApagar={apagarMensagem}
                          onEditar={editarMensagemPrompt}
                        />
                      </div>
                    );
                  })
                )}
              </div>

              <form
                onSubmit={enviar}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 8, padding: 12,
                  borderTop: '1px solid var(--portal-border)',
                  background: 'var(--portal-surface)',
                }}
              >
                {!podeAgir ? (
                  <div style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(240,173,78,0.10)',
                    border: '1px solid rgba(240,173,78,0.4)',
                    display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem',
                  }}>
                    <i className="bi bi-eye" style={{ color: '#f0ad4e', flexShrink: 0 }} />
                    <span>
                      Visualização: este atendimento é do canal <strong>{conversaAtiva.canal_nome}</strong>.
                      Só atendentes desse canal podem responder ou encerrar.
                    </span>
                  </div>
                ) : ninguemAssumiu ? (
                  <button
                    type="button"
                    onClick={assumir}
                    style={{
                      width: '100%', height: 44,
                      background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                      color: '#fff', border: 'none', borderRadius: 8,
                      fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    <i className="bi bi-person-check-fill" /> Assumir conversa para responder
                  </button>
                ) : outroAssumiu ? (
                  <div style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    background: 'rgba(42,138,156,0.08)',
                    border: '1px solid rgba(42,138,156,0.3)',
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontSize: '0.88rem',
                  }}>
                    <i className="bi bi-person-fill" style={{ color: '#2A8A9C', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      Em atendimento por <strong>{conversaAtiva.atendente}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={assumir}
                      style={{
                        background: 'transparent',
                        border: '1px solid #2A8A9C',
                        color: '#2A8A9C',
                        borderRadius: 6, padding: '4px 14px',
                        fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-person-check me-1" />Assumir mesmo assim
                    </button>
                  </div>
                ) : (
                  <>
                    {replyTo && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 8,
                        background: 'rgba(42,138,156,0.1)',
                        borderLeft: '3px solid #2A8A9C',
                        fontSize: '0.82rem',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: '#2A8A9C', marginBottom: 1 }}>
                            <i className="bi bi-reply me-1" />
                            {replyTo.direcao === 'out'
                              ? (replyTo.autor || 'atendente')
                              : (conversaAtiva?.push_name || formatNumero(conversaAtiva?.numero) || 'cliente')}
                          </div>
                          <div style={{ opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {replyTo.conteudo ? String(replyTo.conteudo).slice(0, 100) : `[${replyTo.tipo}]`}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setReplyTo(null)}
                          style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--portal-muted)', cursor: 'pointer', padding: 2, flexShrink: 0,
                          }}
                          title="Cancelar resposta"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    )}
                    {arquivoSelecionado && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 8,
                        background: 'rgba(42,138,156,0.12)',
                        border: '1px solid rgba(42,138,156,0.4)',
                        fontSize: '0.85rem',
                      }}>
                        <i className="bi bi-paperclip" style={{ color: '#2A8A9C' }} />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {arquivoSelecionado.name}
                        </span>
                        <span style={{ color: 'var(--portal-muted)', fontSize: '0.78rem' }}>
                          {(arquivoSelecionado.size / 1024).toFixed(0)} KB
                        </span>
                        <button
                          type="button"
                          onClick={removerArquivo}
                          style={{
                            background: 'transparent', border: 'none',
                            color: 'var(--portal-muted)', cursor: 'pointer', padding: 2,
                          }}
                          title="Remover anexo"
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        ref={inputArquivoRef}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={escolherArquivo}
                      />
                      {gravando ? (
                        <>
                          <button
                            type="button"
                            onClick={cancelarGravacao}
                            title="Cancelar gravacao"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-x-lg" />
                          </button>
                          <div style={{
                            flex: 1, display: 'flex', alignItems: 'center', gap: 10,
                            padding: '0 14px', borderRadius: 8, minHeight: 38,
                            background: 'rgba(231,76,60,0.08)',
                            border: '1px solid rgba(231,76,60,0.4)',
                          }}>
                            <span style={{
                              width: 9, height: 9, borderRadius: '50%',
                              background: '#e74c3c', flexShrink: 0,
                              animation: 'gravacaoPulse 1s ease-in-out infinite',
                            }} />
                            <span style={{ color: '#e74c3c', fontWeight: 600, fontSize: '0.88rem' }}>Gravando</span>
                            <span style={{ fontSize: '0.88rem', opacity: 0.85 }}>{fmtTempoGravacao(tempoGravacao)}</span>
                          </div>
                          <button
                            type="button"
                            onClick={pararEEnviarGravacao}
                            disabled={enviando}
                            style={{
                              background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                              color: '#fff', border: 'none', borderRadius: 8,
                              padding: '0 18px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            {enviando ? '...' : <><i className="bi bi-send-fill me-1" />Enviar</>}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => inputArquivoRef.current?.click()}
                            disabled={enviando}
                            title="Anexar arquivo (max 25MB)"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-paperclip" />
                          </button>
                          <button
                            type="button"
                            onClick={abrirEnviarLoc}
                            disabled={enviando}
                            title="Enviar localizacao"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-geo-alt" />
                          </button>
                          <button
                            type="button"
                            onClick={abrirEnviarContato}
                            disabled={enviando}
                            title="Enviar contato"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-person-lines-fill" />
                          </button>
                          <button
                            type="button"
                            onClick={iniciarGravacao}
                            disabled={enviando || !!arquivoSelecionado}
                            title="Gravar mensagem de voz"
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: 'pointer',
                            }}
                          >
                            <i className="bi bi-mic-fill" />
                          </button>
                          <button
                            type="button"
                            onClick={() => { setRespostaPickerAberto((v) => !v); setRespostaSugestoes(null); }}
                            disabled={enviando || respostasRapidas.length === 0}
                            title={respostasRapidas.length === 0 ? 'Nenhuma resposta rapida cadastrada (Configuracoes)' : 'Respostas rapidas (digite /atalho tambem)'}
                            style={{
                              background: 'transparent',
                              border: '1px solid var(--portal-border)',
                              color: respostasRapidas.length === 0 ? 'var(--portal-muted)' : 'var(--portal-text)',
                              borderRadius: 8, padding: '0 12px', cursor: respostasRapidas.length === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <i className="bi bi-lightning-charge-fill" />
                          </button>
                          <div style={{ position: 'relative', flex: 1 }}>
                            {(respostaSugestoes?.length || respostaPickerAberto) && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%', left: 0, right: 0,
                                marginBottom: 6,
                                background: 'var(--portal-surface)',
                                border: '1px solid var(--portal-border)',
                                borderRadius: 8,
                                boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                                maxHeight: 220, overflowY: 'auto', zIndex: 200,
                              }}>
                                {(respostaPickerAberto
                                  ? (respostasRapidas || []).filter((r) => r.ativo !== false)
                                  : (respostaSugestoes || [])
                                ).map((r) => (
                                  <button
                                    key={r.id}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => aplicarRespostaRapida(r, { substituirToken: !respostaPickerAberto })}
                                    style={{
                                      display: 'block', width: '100%', textAlign: 'left',
                                      padding: '8px 12px', background: 'none', border: 'none',
                                      borderBottom: '1px solid var(--portal-border)', cursor: 'pointer',
                                      color: 'var(--portal-text)',
                                    }}
                                  >
                                    <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>/{r.atalho}</div>
                                    <div style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {r.texto}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            <textarea
                              ref={textareaRef}
                              value={texto}
                              onChange={(e) => {
                                setTexto(e.target.value);
                                atualizarSugestoesRapidas(e.target.value, e.target.selectionStart);
                                if (e.target.value.trim()) sinalizarDigitando();
                              }}
                              onSelect={(e) => atualizarSugestoesRapidas(e.target.value, e.target.selectionStart)}
                              onBlur={() => setTimeout(() => setRespostaSugestoes(null), 150)}
                              placeholder={arquivoSelecionado ? 'Legenda (opcional)...' : 'Digite sua resposta... (Ctrl+V cola imagens; /atalho pra rapidas)'}
                              rows={1}
                              onPaste={aoColar}
                              onKeyDown={(e) => {
                                // Tab autocompleta a 1a sugestao se aberta
                                if (e.key === 'Tab' && respostaSugestoes?.length) {
                                  e.preventDefault();
                                  aplicarRespostaRapida(respostaSugestoes[0], { substituirToken: true });
                                  return;
                                }
                                if (e.key === 'Escape' && (respostaSugestoes || respostaPickerAberto)) {
                                  setRespostaSugestoes(null);
                                  setRespostaPickerAberto(false);
                                  return;
                                }
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  enviar(e);
                                }
                              }}
                              style={{
                                width: '100%', resize: 'none', minHeight: 38, maxHeight: 120,
                                padding: '8px 12px', borderRadius: 8,
                                border: '1px solid var(--portal-border)',
                                background: 'var(--portal-surface)', color: 'var(--portal-text)',
                                fontSize: '0.9rem',
                              }}
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={enviando || (!texto.trim() && !arquivoSelecionado)}
                            style={{
                              background: 'linear-gradient(135deg, #2A8A9C 0%, #1E6370 100%)',
                              color: '#fff', border: 'none', borderRadius: 8,
                              padding: '0 18px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            {enviando ? '...' : <><i className="bi bi-send-fill me-1" />Enviar</>}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </form>
            </>
          ) : (
            <div className="empty-state" style={{ margin: 'auto' }}>
              <strong>Conversa nao disponivel</strong>
              <span>Selecione outra na lista.</span>
            </div>
          )}
        </section>
        )}

        {/* Sidebar de edição de contato — no mobile vira overlay em cima do chat. */}
        {contatoSidebarAberta && conversaAtiva && (
          <aside
            className="content-card"
            style={isMobile
              ? {
                position: 'fixed', inset: 0, zIndex: 4500,
                display: 'flex', flexDirection: 'column', padding: 0,
                overflow: 'hidden', borderRadius: 0,
              }
              : { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', borderBottom: '1px solid var(--portal-border)',
              flexShrink: 0,
            }}>
              <strong style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-person-lines-fill me-2" />
                Editar contato
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setContatoSidebarAberta(false)}
                disabled={contatoSidebarSalvando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {contatoSidebarErro && (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.82rem' }}>
                  {contatoSidebarErro}
                </div>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={contatoSidebarForm.push_name}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, push_name: e.target.value }))}
                  placeholder="Como o contato deve aparecer"
                  autoFocus
                />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Filial (opcional)
                </label>
                <select
                  className="form-select form-select-sm"
                  value={contatoSidebarForm.filial}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, filial: e.target.value }))}
                >
                  <option value="">Nao informar</option>
                  {contatoSidebarForm.filial && !contatoSidebarFiliais.some((f) => f.nome === contatoSidebarForm.filial) && (
                    <option value={contatoSidebarForm.filial}>{contatoSidebarForm.filial}</option>
                  )}
                  {contatoSidebarFiliais.map((f) => (
                    <option key={f.codigo || f.nome} value={f.nome}>
                      {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', display: 'block', marginBottom: 4 }}>
                  Setor (opcional)
                </label>
                <select
                  className="form-select form-select-sm"
                  value={contatoSidebarForm.setor}
                  onChange={(e) => setContatoSidebarForm((f) => ({ ...f, setor: e.target.value }))}
                >
                  <option value="">Nao informar</option>
                  {contatoSidebarForm.setor && !contatoSidebarSetores.some((s) => s.nome === contatoSidebarForm.setor) && (
                    <option value={contatoSidebarForm.setor}>{contatoSidebarForm.setor}</option>
                  )}
                  {contatoSidebarSetores.map((s) => (
                    <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
                  ))}
                </select>
              </div>

              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                <i className="bi bi-telephone me-1" />
                {formatNumero(conversaAtiva.numero)}
                <span style={{ margin: '0 6px' }}>·</span>
                <i className="bi bi-whatsapp me-1" />
                {conversaAtiva.canal_nome}
              </div>
            </div>

            <footer style={{
              padding: '10px 14px', borderTop: '1px solid var(--portal-border)',
              display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setContatoSidebarAberta(false)}
                disabled={contatoSidebarSalvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvarContatoSidebar}
                disabled={contatoSidebarSalvando}
                style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {contatoSidebarSalvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </aside>
        )}
      </div>
    </div>
  );
}
